import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Marker,
  GeoJSON,
  Popup,
  Tooltip,
  useMap,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import NapOverlay from "../components/map/NapOverlay";
import NapPulseMarker from "../components/map/NapPulseMarker";
import MeasureTool from "../components/map/MeasureTool";
import NapDetailsPanel from "../components/common/NapDetailsPanel";
import NapTableModal from "../components/modals/NapTableModal";
import NapDetailsResultModal from "../components/modals/NapDetailsResultModal";

/* Inline styles for map label overlays */
const LABEL_STYLES = document.createElement("style");
LABEL_STYLES.textContent = `
  .ni-city-label-tooltip {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    font-weight: 700;
    font-size: 13px;
    color: #1e3a8a;
    text-shadow: 0 0 4px #fff, 0 0 4px #fff, 0 0 4px #fff;
    white-space: nowrap;
  }
  .ni-city-label-tooltip::before { display: none !important; }
  .ni-brgy-label-icon span {
    font-size: 13px;
    font-weight: 700;
    color: #1d4ed8;
    text-shadow: 0 0 4px #fff, 0 0 4px #fff, 0 0 4px #fff;
    white-space: nowrap;
    pointer-events: none;
    letter-spacing: 0.03em;
  }
  @keyframes fst-pulse {
    0% { transform: scale(1); opacity: 1; }
    100% { transform: scale(2.5); opacity: 0; }
  }
  @keyframes subscriber-pulse {
    0% { transform: scale(1); opacity: 1; }
    100% { transform: scale(1.6); opacity: 0; }
  }
  @keyframes nap-ni-pulse-ring {
    0% { transform: scale(1); opacity: 1; }
    70% { transform: scale(2.5); opacity: 0; }
    100% { transform: scale(2.5); opacity: 0; }
  }
  .measure-tooltip {
    background: rgba(255,255,255,0.95) !important;
    border: 1px solid #f97316 !important;
    border-radius: 4px !important;
    padding: 2px 6px !important;
    box-shadow: 0 1px 4px rgba(0,0,0,0.15) !important;
    font-family: inherit;
    white-space: nowrap;
  }
  .measure-tooltip::before { border-right-color: #f97316 !important; }
`;
if (!document.querySelector("#ni-dash-label-styles")) {
  LABEL_STYLES.id = "ni-dash-label-styles";
  document.head.appendChild(LABEL_STYLES);
}

import api from "../api/client";
import useAuth from "../hooks/useAuth";
import useIsMobile from "../hooks/useIsMobile";
import Navbar from "../components/layout/Navbar";
import MobileNavbar from "../components/layout/MobileNavbar";
import MobileDashboard from "../components/mobile/MobileDashboard";
import TeamAssignmentPopover from "../components/common/TeamAssignmentPopover";
import StatusActionMenu from "../components/StatusActionMenu";
import DateFilter from "../components/dashboard/DateFilter";
import DeployedTeamsPanel from "../components/dashboard/DeployedTeamsPanel";
import NoTeamsPanel from "../components/dashboard/NoTeamsPanel";
import MapPanelTabs from "../components/dashboard/MapPanelTabs";
import { QuickCompleteDialog, QuickCancelDialog } from "../components/modals/QuickNIStatusDialogs";
import RescheduleModal from "../components/modals/RescheduleModal";
import { getCallStatusColor } from "../utils/callStatusOptions";
import { MODULE_THEMES } from "../utils/moduleTheme";
import { geocodeSubscriberAddress } from "../services/geocodingService";
import BARANGAY_COORDS, {
  SERVICE_AREA_CENTER,
  SERVICE_AREA_ZOOM,
  getBarangayCoords,
} from "../data/barangayGeodata";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const NI = MODULE_THEMES.ni;
const HEAD_OFFICE = [14.611065, 121.089339];
const HEAD_OFFICE_ICON = L.divIcon({
  className: "",
  html: `<div style="
    display:flex;align-items:center;justify-content:center;
    width:32px;height:32px;border-radius:50%;
    background:#1e40af;border:3px solid #fff;
    box-shadow:0 2px 8px rgba(0,0,0,0.3);
  "><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg></div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

const STATUS_COLORS = {
  "Active/Complete": "#22c55e",
  Cancelled: "#ef4444",
  Rescheduled: "#f97316",
  Pending: "#eab308",
};

const STATUS_KEYS = [
  ["completed", "Active/Complete"],
  ["cancelled", "Cancelled"],
  ["rescheduled", "Rescheduled"],
  ["pending", "Pending"],
];

// Team marker colors (cycled for multiple teams)
const TEAM_COLORS = [
  "#e11d48", "#7c3aed", "#0891b2", "#059669",
  "#d97706", "#dc2626", "#4f46e5", "#0d9488",
];

// "My Location" icon for FST mobile map — pulsing blue dot
const MY_LOCATION_ICON = L.divIcon({
  className: "",
  html: `<div style="position:relative;width:24px;height:24px;">
    <div style="position:absolute;inset:0;border-radius:50%;background:rgba(59,130,246,0.2);animation:fst-pulse 2s ease-out infinite;"></div>
    <div style="position:absolute;top:4px;left:4px;width:16px;height:16px;border-radius:50%;background:#3b82f6;border:3px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.3);"></div>
  </div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

// Subscriber search pin — distinct magenta/pink marker
const SUBSCRIBER_SEARCH_ICON = L.divIcon({
  className: "",
  html: `<div style="display:flex;flex-direction:column;align-items:center;pointer-events:auto;">
    <div style="
      display:flex;align-items:center;justify-content:center;
      width:60px;height:60px;border-radius:50%;
      background:#eab308;border:4px solid #fff;
      box-shadow:0 4px 14px rgba(234,179,8,0.6);
      animation:subscriber-pulse 2s ease-out infinite;
      --pulse-max-scale:1.4;
    "><svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"/>
      <polyline points="9 22 9 12 15 12 15 22"/>
    </svg></div>
    <div style="width:3px;height:10px;background:#dc2626;"></div>
    <div style="width:10px;height:10px;background:#dc2626;border-radius:50%;border:2px solid #fff;box-shadow:0 2px 4px rgba(220,38,38,0.7);"></div>
  </div>`,
  iconSize: [60, 80],
  iconAnchor: [30, 80],
  popupAnchor: [0, -80],
});

// Smaller subscriber search pin for the mobile FST map
const SUBSCRIBER_SEARCH_ICON_MOBILE = L.divIcon({
  className: "",
  html: `<div style="display:flex;flex-direction:column;align-items:center;pointer-events:auto;">
    <div style="
      display:flex;align-items:center;justify-content:center;
      width:38px;height:38px;border-radius:50%;
      background:#eab308;border:3px solid #fff;
      box-shadow:0 3px 10px rgba(234,179,8,0.6);
      animation:subscriber-pulse 2s ease-out infinite;
      --pulse-max-scale:1.4;
    "><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"/>
      <polyline points="9 22 9 12 15 12 15 22"/>
    </svg></div>
    <div style="width:3px;height:8px;background:#dc2626;"></div>
    <div style="width:10px;height:10px;background:#dc2626;border-radius:50%;border:2px solid #fff;box-shadow:0 2px 4px rgba(220,38,38,0.7);"></div>
  </div>`,
  iconSize: [38, 56],
  iconAnchor: [19, 56],
  popupAnchor: [0, -56],
});

// FST location icon for desktop dashboard — person marker
function buildFSTIcon(isActive) {
  const bg = isActive ? "#3b82f6" : "#9ca3af";
  return L.divIcon({
    className: "",
    html: `<div style="display:flex;flex-direction:column;align-items:center;pointer-events:auto;">
      <div style="
        display:flex;align-items:center;justify-content:center;
        width:28px;height:28px;border-radius:50%;
        background:${bg};border:2.5px solid #fff;
        box-shadow:0 2px 6px rgba(0,0,0,0.35);
      "><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
        <circle cx="12" cy="7" r="4"/>
      </svg></div>
    </div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
  });
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function getLocalToday() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function FlyTo({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo(center, zoom || 15, { duration: 0.8 });
  }, [center, zoom, map]);
  return null;
}

// Pans the map to a coordinate WITHOUT changing the current zoom level.
// Used when a job order is clicked from the Deployed Teams panel. `target`
// carries a `nonce` so repeated clicks on the same JO re-trigger the pan.
function PanTo({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target && target.lat != null && target.lng != null) {
      map.panTo([target.lat, target.lng]);
    }
  }, [target, map]);
  return null;
}

function NiMapZoomTracker({ onZoomChange }) {
  const map = useMapEvents({
    zoomend() { onZoomChange(map.getZoom()); },
  });
  useEffect(() => { onZoomChange(map.getZoom()); }, [map, onZoomChange]);
  return null;
}

function ZoomWatcher({ onZoomChange }) {
  useMapEvents({
    zoomend(e) {
      onZoomChange(e.target.getZoom());
    },
  });
  return null;
}

function MapClickToPlace({ active, onPlaced }) {
  const enabledAtRef = useRef(0);
  useEffect(() => {
    if (active) enabledAtRef.current = Date.now();
  }, [active]);
  useMapEvents({
    click(e) {
      if (!active) return;
      if (Date.now() - enabledAtRef.current < 100) return;
      if (e.originalEvent?.target?.closest(".leaflet-popup, .leaflet-control")) return;
      onPlaced(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function FitMarkerBounds({ records }) {
  const map = useMap();
  useEffect(() => {
    const pts = (records || []).filter((r) => r.latitude && r.longitude);
    if (pts.length === 0) return;
    const bounds = L.latLngBounds(pts.map((r) => [r.latitude, r.longitude]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
  }, [records, map]);
  return null;
}

function BrgyLabels({ data }) {
  const map = useMap();
  useEffect(() => {
    if (!data || !map) return;
    const markers = [];
    (data.features || []).forEach((f) => {
      const name = f.properties?.brgy_name || "";
      if (!name) return;
      const centroid = L.geoJSON(f).getBounds().getCenter();
      const marker = L.marker(centroid, {
        icon: L.divIcon({
          className: "",
          html: `<span style="font-size:13px;font-weight:700;color:#1d4ed8;text-shadow:0 0 4px #fff,0 0 4px #fff,0 0 4px #fff;white-space:nowrap;pointer-events:none;letter-spacing:0.03em;">${name}</span>`,
          iconSize: [0, 0],
          iconAnchor: [0, 0],
        }),
        interactive: false,
      });
      marker.addTo(map);
      markers.push(marker);
    });
    return () => markers.forEach((m) => map.removeLayer(m));
  }, [data, map]);
  return null;
}

function circleRadius(total) {
  if (total >= 300) return 22;
  if (total >= 100) return 16;
  if (total >= 50) return 13;
  if (total >= 20) return 10;
  return 7;
}

// ---------------------------------------------------------------------------
// Pie chart icon builder
// ---------------------------------------------------------------------------
const pieIconCache = {};

function buildPieIcon(brgy, size) {
  const vals = STATUS_KEYS.map(([k]) => brgy[k] || 0).join(",");
  const cacheKey = `ni-${brgy.municipality}-${brgy.barangay}-${size}-${vals}`;
  if (pieIconCache[cacheKey]) return pieIconCache[cacheKey];

  const slices = [];
  let total = 0;
  for (const [key, label] of STATUS_KEYS) {
    const val = brgy[key] || 0;
    if (val > 0) {
      slices.push({ label, value: val, color: STATUS_COLORS[label] });
      total += val;
    }
  }

  const r = size / 2;
  const cx = r;
  const cy = r;
  let svg;

  if (slices.length <= 1) {
    const color = slices.length === 1 ? slices[0].color : "#9ca3af";
    svg = `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
      <circle cx="${cx}" cy="${cy}" r="${r - 1}" fill="${color}" fill-opacity="0.8" stroke="#fff" stroke-width="1.5"/>
    </svg>`;
  } else {
    let paths = "";
    let angle = -Math.PI / 2;
    for (const slice of slices) {
      const sliceAngle = (slice.value / total) * 2 * Math.PI;
      const x1 = cx + (r - 1) * Math.cos(angle);
      const y1 = cy + (r - 1) * Math.sin(angle);
      const x2 = cx + (r - 1) * Math.cos(angle + sliceAngle);
      const y2 = cy + (r - 1) * Math.sin(angle + sliceAngle);
      const largeArc = sliceAngle > Math.PI ? 1 : 0;
      paths += `<path d="M${cx},${cy} L${x1},${y1} A${r - 1},${r - 1} 0 ${largeArc} 1 ${x2},${y2} Z" fill="${slice.color}" fill-opacity="0.8"/>`;
      angle += sliceAngle;
    }
    svg = `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
      ${paths}
      <circle cx="${cx}" cy="${cy}" r="${r - 1}" fill="none" stroke="#fff" stroke-width="1.5"/>
    </svg>`;
  }

  const icon = L.divIcon({
    className: "",
    html: svg,
    iconSize: [size, size],
    iconAnchor: [r, r],
  });
  pieIconCache[cacheKey] = icon;
  return icon;
}

// ---------------------------------------------------------------------------
// FST Mobile Dashboard — simplified view for field technicians
// ---------------------------------------------------------------------------
function FSTDashboard() {
  const { myLocation, fstLocationError, fstLocationPermission, hasPerm } = useAuth();
  const [viewMode, setViewMode] = useState("page"); // "map" | "page"
  const [mapRecords, setMapRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(() => getLocalToday());
  const [flyTarget, setFlyTarget] = useState(null);
  const [flyZoom, setFlyZoom] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [fstBaseMap, setFstBaseMap] = useState("satellite"); // "road" | "satellite"
  const canViewNapOnFstMap = hasPerm("nap_locator.view_on_ni_map");
  const [fstNapEnabled, setFstNapEnabled] = useState(true);
  const [fstShowLabels, setFstShowLabels] = useState(true);
  const [fstLayerOpen, setFstLayerOpen] = useState(false);
  const [selectedNap, setSelectedNap] = useState(null);
  const [napRecord, setNapRecord] = useState(null);
  const [napRecordLoading, setNapRecordLoading] = useState(false);
  const [fstQrView, setFstQrView] = useState(null);
  const [fstFetchingNapDetails, setFstFetchingNapDetails] = useState(false);
  const [fstNapDetailsModal, setFstNapDetailsModal] = useState(null);
  const [fstNapTableModalSearch, setFstNapTableModalSearch] = useState(null);
  const fstAbortRef = useRef(null);

  // Measure tool state
  const [fstMeasureEnabled, setFstMeasureEnabled] = useState(false);
  const [fstMeasureTotal, setFstMeasureTotal] = useState(0);
  const [fstMeasureDrawing, setFstMeasureDrawing] = useState(true);
  const [fstMeasureDoneSignal, setFstMeasureDoneSignal] = useState(0);
  const [fstMeasureUndoSignal, setFstMeasureUndoSignal] = useState(0);

  // Subscriber locate / edit-location state (parity with Migration mobile map)
  const [subscriberPin, setSubscriberPin] = useState(null);
  const [subscriberLocating, setSubscriberLocating] = useState(false);
  const [subscriberLocateMsg, setSubscriberLocateMsg] = useState(null);
  const [editingLocation, setEditingLocation] = useState(false);
  const [editLocConfirm, setEditLocConfirm] = useState(false);
  const [editLocSaving, setEditLocSaving] = useState(false);
  const [locateTrigger, setLocateTrigger] = useState(0);
  const pendingLocateRef = useRef(null);
  const locateExecuted = useRef(false);

  // Start locating a subscriber — used both by the Page list (name click) and by
  // the in-map record detail (name click). Switches to the map and drops an
  // editable pin so the location can be updated from anywhere.
  const startLocate = useCallback((rec) => {
    if (!rec) return;
    // Enrich coords from the loaded map records (the list payload lacks lat/lng)
    const full = mapRecords.find((m) => m.id === rec.id) || {};
    pendingLocateRef.current = {
      id: rec.id,
      customerName: rec.name || rec.customer_full_name,
      address: rec.complete_address || rec.address || full.complete_address || "",
      municipality: rec.municipality || full.municipality,
      barangay: rec.barangay || full.barangay,
      latitude: rec.latitude ?? full.latitude ?? null,
      longitude: rec.longitude ?? full.longitude ?? null,
      locationSaved: rec.location_saved ?? full.location_saved ?? false,
    };
    locateExecuted.current = false;
    setSelectedRecord(null);
    setSelectedNap(null);
    setSubscriberPin(null);
    setEditingLocation(false);
    setEditLocConfirm(false);
    setSubscriberLocateMsg(null);
    setViewMode("map");
    setLocateTrigger((c) => c + 1);
  }, [mapRecords]);

  // Auto-dismiss locate message after 5s
  useEffect(() => {
    if (!subscriberLocateMsg) return;
    const t = setTimeout(() => setSubscriberLocateMsg(null), 5000);
    return () => clearTimeout(t);
  }, [subscriberLocateMsg]);

  // Fetch NAP details when a NAP point is clicked
  useEffect(() => {
    if (!selectedNap?.id) { setNapRecord(null); return; }
    setNapRecordLoading(true);
    api.get("/nap-locator", { params: { search: selectedNap.nap_code, page: 1, page_size: 1 } })
      .then((res) => { setNapRecord(res.data.find((r) => r.id === selectedNap.id) || res.data[0] || null); })
      .catch(() => setNapRecord(null))
      .finally(() => setNapRecordLoading(false));
  }, [selectedNap]);

  // Fetch assigned JO map records
  const fetchData = useCallback(async () => {
    if (fstAbortRef.current) fstAbortRef.current.abort();
    const controller = new AbortController();
    fstAbortRef.current = controller;

    setLoading(true);
    try {
      const params = {};
      if (dateFrom) { params.date_from = dateFrom; params.date_to = dateFrom; }
      const { data } = await api.get("/ni/dashboard/map-records", { params, signal: controller.signal });
      setMapRecords(data);
    } catch (err) {
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
      setMapRecords([]);
    } finally { setLoading(false); }
  }, [dateFrom]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Stats from records
  const stats = useMemo(() => {
    const c = { Pending: 0, "Active/Complete": 0, Rescheduled: 0, Cancelled: 0 };
    for (const r of mapRecords) c[r.status] = (c[r.status] || 0) + 1;
    return c;
  }, [mapRecords]);

  // Process a pending subscriber locate request: fly to saved/recorded coords,
  // else show the barangay area and geocode the address. Drops an editable pin.
  useEffect(() => {
    if (loading) return;
    const req = pendingLocateRef.current;
    if (!req) return;
    if (locateExecuted.current) return;
    locateExecuted.current = true;
    pendingLocateRef.current = null;

    const hasCoords = req.latitude && req.longitude && !isNaN(req.latitude) && !isNaN(req.longitude);
    let immediateCoords = null;

    if (hasCoords) {
      immediateCoords = [parseFloat(req.latitude), parseFloat(req.longitude)];
    } else {
      immediateCoords = getBarangayCoords(req.municipality, req.barangay);
    }

    if (immediateCoords) {
      const pin = {
        id: req.id || null,
        lat: immediateCoords[0],
        lng: immediateCoords[1],
        name: req.customerName,
        address: req.address,
        municipality: req.municipality,
        barangay: req.barangay,
      };
      setSubscriberPin(pin);
      setSelectedRecord(null);
      setSelectedNap(null);
      setFlyTarget(immediateCoords);
      setFlyZoom(hasCoords ? 18 : 16);
      setEditingLocation(true);
      setEditLocConfirm(false);
      if (hasCoords) {
        setSubscriberLocateMsg({ type: "success", text: "Subscriber location found." });
        setSubscriberLocating(false);
        return; // saved or recorded coords — no need for geocoding
      }
      setSubscriberLocating(true);
      setSubscriberLocateMsg({ type: "warning", text: "Showing barangay area. Searching for exact address…" });
    } else {
      setSubscriberLocating(true);
      setSubscriberLocateMsg(null);
    }

    // Phase 2: Geocode the address for a precise location
    const refineLocation = async () => {
      try {
        const result = await geocodeSubscriberAddress({
          address: req.address,
          barangay: req.barangay,
          municipality: req.municipality,
          latitude: null,
          longitude: null,
        });

        if (result.success && result.latitude && result.longitude) {
          const pin = {
            id: req.id || null,
            lat: result.latitude,
            lng: result.longitude,
            name: req.customerName,
            address: req.address,
            municipality: req.municipality,
            barangay: req.barangay,
          };
          setSubscriberPin(pin);
          setFlyTarget([result.latitude, result.longitude]);
          setFlyZoom(18);
          setEditingLocation(true);
          setEditLocConfirm(false);
          const msgType = (result.accuracy === "exact" || result.accuracy === "address") ? "success" : "warning";
          setSubscriberLocateMsg({ type: msgType, text: result.message });
        } else if (!immediateCoords) {
          setSubscriberLocateMsg({ type: "error", text: result.message });
        } else {
          setEditingLocation(true);
          setEditLocConfirm(false);
          setSubscriberLocateMsg({ type: "warning", text: "Exact address not found. Showing barangay area." });
        }
      } catch {
        if (!immediateCoords) {
          setSubscriberLocateMsg({ type: "error", text: "Unable to locate subscriber address." });
        } else {
          setEditingLocation(true);
          setEditLocConfirm(false);
          setSubscriberLocateMsg({ type: "warning", text: "Exact address not found. Showing barangay area." });
        }
      } finally {
        setSubscriberLocating(false);
      }
    };

    refineLocation();
  }, [loading, locateTrigger]);

  // Handler: fetch NAP details from S2S API
  const handleFstNapDetails = async () => {
    if (!napRecord) return;
    setFstFetchingNapDetails(true);
    try {
      const { data } = await api.post(`/nap-locator/${napRecord.id}/nap-details`);
      if (data.found) {
        setFstNapDetailsModal(data);
      } else {
        alert(data.detail || `No NAP details found for "${napRecord.nap_code}" on the external server.`);
      }
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to fetch NAP details from external server.");
    } finally {
      setFstFetchingNapDetails(false);
    }
  };

  // Handler: view on table
  const handleFstViewOnTable = () => {
    if (!napRecord) return;
    const parts = napRecord.nap_code.trim().split(/\s+/);
    const lcp = parts.length >= 2 ? `${parts[0]} ${parts[1]}` : napRecord.nap_code;
    setFstNapTableModalSearch(lcp);
  };

  const navigateDate = (days) => {
    const d = new Date(dateFrom + "T00:00:00");
    d.setDate(d.getDate() + days);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    setDateFrom(`${y}-${m}-${dd}`);
    setSelectedRecord(null);
    setFlyTarget(null);
    clearLocate();
  };

  // Clear any in-progress subscriber locate / edit state
  const clearLocate = () => {
    pendingLocateRef.current = null;
    locateExecuted.current = false;
    setSubscriberPin(null);
    setEditingLocation(false);
    setEditLocConfirm(false);
    setSubscriberLocating(false);
    setSubscriberLocateMsg(null);
  };

  // Open the map and fly to a NAP location (triggered from a JO detail's NAP code).
  // Switches to the map view immediately, then looks up the NAP coordinates.
  const focusNapOnMap = async (napCode) => {
    if (!napCode) return;
    clearLocate();
    setSelectedRecord(null);
    setViewMode("map");
    setSubscriberLocateMsg({ type: "warning", text: "Locating NAP…" });
    try {
      const { data } = await api.get("/nap-locator", {
        params: { search: napCode, page: 1, page_size: 20, sort_by: "nap_code", sort_dir: "asc" },
      });
      const norm = (s) => String(s || "").trim().toUpperCase().replace(/\s+/g, " ");
      const match = (Array.isArray(data) ? data : []).find((o) => norm(o.nap_code) === norm(napCode));
      if (!match) { setSubscriberLocateMsg({ type: "error", text: "NAP code not found in NAP Locator." }); return; }
      if (match.latitude == null || match.longitude == null) { setSubscriberLocateMsg({ type: "error", text: "This NAP has no coordinates set." }); return; }
      if (canViewNapOnFstMap) setFstNapEnabled(true);
      setSelectedNap({ id: match.id, nap_code: match.nap_code, latitude: match.latitude, longitude: match.longitude });
      setFlyTarget([match.latitude, match.longitude]);
      setFlyZoom(18);
      setSubscriberLocateMsg(null);
    } catch {
      setSubscriberLocateMsg({ type: "error", text: "Failed to look up NAP location." });
    }
  };

  // Page View — render MobileDashboard with a toggle strip above it
  if (viewMode === "page") {
    return (
      <MobileDashboard
        initialDate={dateFrom}
        onDateChange={(d) => setDateFrom(d)}
        onCustomerLocationClick={startLocate}
        onNapLocate={focusNapOnMap}
        headerExtra={
          <div className="flex flex-shrink-0 ml-2">
            <button onClick={() => setViewMode("map")} className="px-3 py-2 text-xs font-semibold rounded-l-lg border bg-white text-gray-600">Map</button>
            <button className={`px-3 py-2 text-xs font-semibold rounded-r-lg border ${NI.btnPrimaryStatic}`}>Page</button>
          </div>
        }
      />
    );
  }

  // Map View
  return (
    <div className="h-screen flex flex-col bg-gray-50">
      <MobileNavbar />

      {/* Date nav + toggle */}
      <div className="flex-shrink-0 px-3 pt-2 pb-1 flex items-center gap-1.5">
        <button type="button" onClick={() => navigateDate(-1)} className={`flex-shrink-0 text-xs text-white ${NI.btnPrimaryStatic} w-8 h-8 rounded font-bold flex items-center justify-center`}>&lt;</button>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => { setDateFrom(e.target.value); setSelectedRecord(null); clearLocate(); }}
          className="flex-1 border rounded-lg px-2 py-1 text-xs bg-white min-w-0"
        />
        <button type="button" onClick={() => navigateDate(1)} className={`flex-shrink-0 text-xs text-white ${NI.btnPrimaryStatic} w-8 h-8 rounded font-bold flex items-center justify-center`}>&gt;</button>

        <div className="flex flex-shrink-0 ml-1">
          <button
            type="button"
            onClick={() => setViewMode("map")}
            className={`px-3 py-1.5 text-[10px] font-semibold rounded-l-lg border ${NI.btnPrimaryStatic}`}
          >
            Map
          </button>
          <button
            type="button"
            onClick={() => { clearLocate(); setViewMode("page"); }}
            className="px-3 py-1.5 text-[10px] font-semibold rounded-r-lg border bg-white text-gray-600"
          >
            Page
          </button>
        </div>
      </div>

      {/* Location permission banner */}
      {fstLocationPermission === "denied" && fstLocationError && (
        <div className="flex-shrink-0 mx-3 mb-1 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-[11px] text-amber-800 leading-snug">
          {fstLocationError}
        </div>
      )}

      {/* Status cards */}
      <div className="flex-shrink-0 grid grid-cols-4 gap-1.5 px-3 pb-1.5">
        {[
          { label: "Pending", key: "Pending", color: "bg-yellow-50 text-yellow-800 border-yellow-200" },
          { label: "Done", key: "Active/Complete", color: "bg-green-50 text-green-800 border-green-200" },
          { label: "Resched", key: "Rescheduled", color: "bg-orange-50 text-orange-800 border-orange-200" },
          { label: "Cancel", key: "Cancelled", color: "bg-red-50 text-red-800 border-red-200" },
        ].map(({ label, key, color }) => (
          <div key={key} className={`rounded-lg px-1 py-1.5 text-center border text-xs font-medium ${color}`}>
            <p className="text-[9px] uppercase tracking-wide">{label}</p>
            <p className="text-sm font-bold">{stats[key] || 0}</p>
          </div>
        ))}
      </div>

      {/* Map — isolate keeps Leaflet z-indexes below the navbar/menus */}
      <div className="flex-1 relative isolate">
        {loading ? (
          <div className="flex items-center justify-center h-full text-gray-400 text-sm">Loading…</div>
        ) : (
          <MapContainer
            center={SERVICE_AREA_CENTER}
            zoom={SERVICE_AREA_ZOOM}
            maxZoom={21}
            style={{ height: "100%", width: "100%", background: "#e5e7eb", cursor: editingLocation && !editLocConfirm ? "crosshair" : undefined }}
            scrollWheelZoom
          >
            {/* Base map — Road (fast) or Satellite */}
            {fstBaseMap === "satellite" ? (
              <TileLayer
                key="sat"
                attribution="&copy; Google"
                url="https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
                maxZoom={21}
              />
            ) : (
              <TileLayer
                key="road"
                attribution='&copy; <a href="https://carto.com/">CARTO</a> &copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
                url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager_nolabels/{z}/{x}/{y}{r}.png"
              />
            )}
            {/* Street names overlay */}
            {fstShowLabels && (
              <TileLayer
                key="labels"
                attribution="&copy; Google"
                url="https://mt1.google.com/vt/lyrs=h&x={x}&y={y}&z={z}"
                maxZoom={21}
                zIndex={500}
              />
            )}
            {flyTarget && <FlyTo center={flyTarget} zoom={flyZoom} />}
            {!flyTarget && <FitMarkerBounds records={mapRecords} />}

            {/* JO house markers */}
            {mapRecords.filter((r) => r.latitude && r.longitude).map((r) => {
              const color = STATUS_COLORS[r.status] || "#9ca3af";
              return (
                <Marker
                  key={`fst-${r.id}`}
                  position={[r.latitude, r.longitude]}
                  pane="markerPane"
                  icon={L.divIcon({
                    className: "",
                    html: `<div style="display:flex;flex-direction:column;align-items:center;pointer-events:auto;">
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" style="filter:drop-shadow(0 1px 2px rgba(0,0,0,0.4));">
                        <path d="M12 3L2 12h3v8h6v-5h2v5h6v-8h3L12 3z" fill="${color}" stroke="#fff" stroke-width="1.5"/>
                      </svg>
                    </div>`,
                    iconSize: [18, 18],
                    iconAnchor: [9, 18],
                  })}
                  eventHandlers={{
                    click: () => {
                      setSelectedRecord(r);
                      setSelectedNap(null);
                      setFlyTarget([r.latitude, r.longitude]);
                      setFlyZoom(17);
                    },
                  }}
                >
                  <Tooltip direction="top" offset={[0, -18]}>
                    <div className="text-xs">
                      <div className="font-bold">{r.jo_number || r.account_number}</div>
                      <div>{r.name}</div>
                      <div className="flex items-center gap-1 mt-0.5">
                        <span className="inline-block w-2 h-2 rounded-full" style={{ background: color }} />
                        {r.status}
                      </div>
                    </div>
                  </Tooltip>
                </Marker>
              );
            })}

            {/* My Location marker */}
            {myLocation && (
              <Marker
                position={[myLocation.latitude, myLocation.longitude]}
                icon={MY_LOCATION_ICON}
                pane="markerPane"
              >
                <Tooltip direction="top" offset={[0, -20]} permanent>
                  <span className="text-[10px] font-bold text-blue-700">You</span>
                </Tooltip>
              </Marker>
            )}

            {/* NAP overlay — visible at zoom 16+ if user has permission */}
            {canViewNapOnFstMap && (
              <NapOverlay
                enabled={fstNapEnabled}
                onPointClick={(pt) => { setSelectedNap(pt); setSelectedRecord(null); }}
                selectedNapId={selectedNap?.id || null}
              />
            )}

            {/* Subscriber locate pin */}
            {subscriberPin && (
              <Marker position={[subscriberPin.lat, subscriberPin.lng]} icon={SUBSCRIBER_SEARCH_ICON_MOBILE} zIndexOffset={2000}>
                <Popup>
                  <div className="text-xs space-y-0.5">
                    <div className="font-bold text-gray-800">{subscriberPin.name}</div>
                    {subscriberPin.address && <div className="text-gray-600">{subscriberPin.address}</div>}
                  </div>
                </Popup>
              </Marker>
            )}

            {/* Click-to-repin while editing location */}
            <MapClickToPlace
              active={editingLocation && !editLocConfirm}
              onPlaced={(lat, lng) => {
                setSubscriberPin((prev) => prev ? { ...prev, lat, lng } : null);
              }}
            />

            {/* Measure tool */}
            <MeasureTool
              enabled={fstMeasureEnabled}
              onTotalChange={setFstMeasureTotal}
              onDrawingChange={setFstMeasureDrawing}
              doneSignal={fstMeasureDoneSignal}
              undoSignal={fstMeasureUndoSignal}
            />
          </MapContainer>
        )}

        {/* Subscriber locating spinner */}
        {subscriberLocating && (
          <div className="absolute top-2 left-2 z-[1100] bg-white/90 rounded-lg px-3 py-2 shadow flex items-center gap-2">
            <svg className="animate-spin h-4 w-4 text-blue-500" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
            <span className="text-xs text-gray-600">Locating…</span>
          </div>
        )}

        {/* Subscriber locate message */}
        {subscriberLocateMsg && !subscriberLocating && (
          <div className={`absolute top-2 left-2 z-[1100] rounded-lg px-3 py-2 shadow text-xs font-medium ${
            subscriberLocateMsg.type === "success" ? "bg-green-100 text-green-800" :
            subscriberLocateMsg.type === "warning" ? "bg-yellow-100 text-yellow-800" :
            "bg-red-100 text-red-800"
          }`}>
            {subscriberLocateMsg.text}
          </div>
        )}

        {/* Editing location toolbar — pinned to the visual viewport bottom with
            `fixed` (not `absolute`) so it stays visible on every phone. Using
            `absolute bottom-0` inside the h-screen map container pushed it below
            the fold on browsers where 100vh exceeds the visible viewport. The
            safe-area padding keeps the buttons clear of the iOS home indicator /
            Android gesture bar. */}
        {editingLocation && subscriberPin && (
          <div
            className="fixed bottom-0 left-0 right-0 z-[2000] bg-gray-900/95 text-white px-4 pt-3 flex items-center gap-2 flex-wrap shadow-[0_-4px_12px_rgba(0,0,0,0.35)]"
            style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
          >
            {!editLocConfirm ? (
              <>
                <span className="text-xs flex-1">📍 Tap on map to adjust pin</span>
                <button
                  onClick={() => setEditLocConfirm(true)}
                  disabled={!subscriberPin}
                  className="px-3 py-1.5 bg-emerald-500 active:bg-emerald-400 disabled:opacity-40 rounded text-xs font-semibold"
                >Save</button>
                <button
                  onClick={() => { setEditingLocation(false); setEditLocConfirm(false); setSubscriberPin(null); setSubscriberLocateMsg(null); }}
                  className="px-3 py-1.5 bg-white/20 active:bg-white/30 rounded text-xs"
                >Cancel</button>
              </>
            ) : (
              <>
                <span className="text-xs flex-1">Save this as subscriber's location?</span>
                <button
                  onClick={async () => {
                    const recId = subscriberPin?.id;
                    if (!recId || !subscriberPin) return;
                    setEditLocSaving(true);
                    try {
                      await api.put(`/ni/dashboard/${recId}/coordinates`, {
                        latitude: subscriberPin.lat,
                        longitude: subscriberPin.lng,
                      });
                      setMapRecords((prev) =>
                        prev.map((rec) => rec.id === recId ? { ...rec, latitude: subscriberPin.lat, longitude: subscriberPin.lng, location_saved: true } : rec)
                      );
                      setSubscriberLocateMsg({ type: "success", text: "Location saved." });
                      setSubscriberPin(null);
                      setEditingLocation(false);
                      setEditLocConfirm(false);
                    } catch {
                      setSubscriberLocateMsg({ type: "error", text: "Failed to save coordinates." });
                      setEditLocConfirm(false);
                    } finally {
                      setEditLocSaving(false);
                    }
                  }}
                  disabled={editLocSaving}
                  className="px-3 py-1.5 bg-emerald-500 active:bg-emerald-400 disabled:opacity-50 rounded text-xs font-semibold"
                >{editLocSaving ? "Saving…" : "Confirm"}</button>
                <button
                  onClick={() => setEditLocConfirm(false)}
                  disabled={editLocSaving}
                  className="px-3 py-1.5 bg-white/20 active:bg-white/30 disabled:opacity-50 rounded text-xs"
                >Back</button>
              </>
            )}
          </div>
        )}

        {/* Measure tool controls — mobile NI map */}
        {!loading && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 z-[1000] flex items-center gap-1">
            {!fstMeasureEnabled ? (
              <button
                type="button"
                onClick={() => setFstMeasureEnabled(true)}
                className="flex items-center gap-1 px-2 py-1.5 rounded-lg shadow-md border border-gray-200 bg-white text-xs font-medium text-gray-700 active:bg-gray-100"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21l6-6m0 0l4-4m-4 4l-4-4m4 4l4 4M21 3l-6 6m0 0l-4 4m4-4l4 4m-4-4l-4-4" />
                </svg>
                Measure
              </button>
            ) : (
              <>
                <div className="flex items-center gap-1 px-2 py-1.5 rounded-lg shadow-md border border-orange-300 bg-orange-500 text-xs font-bold text-white">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21l6-6m0 0l4-4m-4 4l-4-4m4 4l4 4M21 3l-6 6m0 0l-4 4m4-4l4 4m-4-4l-4-4" />
                  </svg>
                  {fstMeasureTotal < 1000 ? fstMeasureTotal.toFixed(1) + " m" : (fstMeasureTotal / 1000).toFixed(2) + " km"}
                </div>
                {fstMeasureDrawing && (
                  <>
                    <button
                      type="button"
                      onClick={() => setFstMeasureUndoSignal((v) => v + 1)}
                      className="px-2 py-1.5 rounded-lg shadow-md border border-gray-200 bg-white text-xs font-medium text-gray-600 active:bg-gray-100"
                      title="Undo last point"
                    >
                      Undo
                    </button>
                    <button
                      type="button"
                      onClick={() => setFstMeasureDoneSignal((v) => v + 1)}
                      className="px-2 py-1.5 rounded-lg shadow-md border border-green-300 bg-green-500 text-xs font-bold text-white active:bg-green-600"
                    >
                      Done
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => { setFstMeasureEnabled(false); setFstMeasureTotal(0); }}
                  className="px-1.5 py-1.5 rounded-lg shadow-md border border-gray-200 bg-white text-gray-500 active:bg-red-50 active:text-red-500"
                  title="Clear & close"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </>
            )}
          </div>
        )}

        {/* Street Names checkbox — mobile NI map */}
        {!loading && (
          <div className="absolute top-[90px] left-[10px] z-[1000]">
            <label className="flex items-center gap-1.5 bg-white rounded-lg shadow-md border border-gray-200 px-2 py-1.5 cursor-pointer active:bg-gray-50">
              <input
                type="checkbox"
                checked={fstShowLabels}
                onChange={(e) => setFstShowLabels(e.target.checked)}
                className="w-3.5 h-3.5 rounded"
              />
              <span className="text-[11px] font-medium text-gray-700">Street Names</span>
            </label>
          </div>
        )}

        {/* Map layer control — mobile FST only */}
        {!loading && (
          <div className="absolute top-2 right-2 z-[1000]">
            <button
              type="button"
              onClick={() => setFstLayerOpen((v) => !v)}
              className="w-9 h-9 bg-white rounded-lg shadow-md border border-gray-200 flex items-center justify-center active:bg-gray-100"
              aria-label="Map layers"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 2 7 12 12 22 7 12 2" />
                <polyline points="2 17 12 22 22 17" />
                <polyline points="2 12 12 17 22 12" />
              </svg>
            </button>
            {fstLayerOpen && (
              <div className="mt-1.5 bg-white rounded-xl shadow-lg border border-gray-200 p-3 w-[170px] animate-in fade-in">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Base Map</p>
                <div className="flex gap-1 mb-3">
                  <button
                    type="button"
                    onClick={() => setFstBaseMap("road")}
                    className={`flex-1 px-2 py-1.5 text-[11px] font-semibold rounded-lg transition-colors ${
                      fstBaseMap === "road" ? "bg-blue-600 text-white shadow-sm" : "bg-gray-100 text-gray-600 active:bg-gray-200"
                    }`}
                  >
                    Road
                  </button>
                  <button
                    type="button"
                    onClick={() => setFstBaseMap("satellite")}
                    className={`flex-1 px-2 py-1.5 text-[11px] font-semibold rounded-lg transition-colors ${
                      fstBaseMap === "satellite" ? "bg-blue-600 text-white shadow-sm" : "bg-gray-100 text-gray-600 active:bg-gray-200"
                    }`}
                  >
                    Satellite
                  </button>
                </div>
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Street Names</p>
                <button
                  type="button"
                  onClick={() => setFstShowLabels((v) => !v)}
                  className={`w-full px-2 py-1.5 text-[11px] font-semibold rounded-lg transition-colors ${
                    fstShowLabels ? "bg-blue-600 text-white shadow-sm" : "bg-gray-100 text-gray-600 active:bg-gray-200"
                  }`}
                >
                  {fstShowLabels ? "ON" : "OFF"}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Selected JO detail overlay */}
        {selectedRecord && !editingLocation && (
          <div className="absolute bottom-0 left-0 right-0 z-[1000] bg-white border-t shadow-lg rounded-t-xl max-h-[45%] overflow-y-auto">
            <div className="flex items-center justify-between px-4 pt-3 pb-1">
              <h3 className="text-sm font-bold text-gray-800 truncate">{selectedRecord.jo_number || selectedRecord.account_number}</h3>
              <button onClick={() => setSelectedRecord(null)} className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
            </div>
            <div className="px-4 pb-4 space-y-1.5 text-xs text-gray-700">
              <div>
                <span className="text-gray-400">Name:</span>{" "}
                <span
                  className="font-semibold text-blue-600 cursor-pointer"
                  onClick={() => startLocate(selectedRecord)}
                >
                  {selectedRecord.name || "—"}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-gray-400">Status:</span>
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: STATUS_COLORS[selectedRecord.status] || "#9ca3af" }} />
                <span className="font-medium">{selectedRecord.status}</span>
              </div>
              {(selectedRecord.complete_address || selectedRecord.address) && <div><span className="text-gray-400">Address:</span> {selectedRecord.complete_address || selectedRecord.address}</div>}
              <div><span className="text-gray-400">Barangay:</span> {selectedRecord.barangay || "—"}</div>
              <div><span className="text-gray-400">Municipality:</span> {(selectedRecord.municipality || "").replace(/^CITY OF\s+/i, "")}</div>
              {selectedRecord.team_number && <div><span className="text-gray-400">Team:</span> {selectedRecord.team_number}</div>}
              {selectedRecord.plan && <div><span className="text-gray-400">Plan:</span> {selectedRecord.plan}</div>}
            </div>
          </div>
        )}

        {/* Selected NAP detail overlay */}
        {selectedNap && !selectedRecord && (
          <div className="absolute bottom-0 left-0 right-0 z-[1500] pointer-events-none">
            <div className="pointer-events-auto w-full max-h-[55vh] bg-white rounded-t-2xl shadow-xl overflow-y-auto border-t">
              <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">NAP Details</span>
                <button onClick={() => setSelectedNap(null)} className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 active:bg-gray-200 text-lg leading-none">&times;</button>
              </div>
              <div className="px-4 py-3">
                {napRecordLoading ? (
                  <p className="text-sm text-gray-400 py-6 text-center">Loading...</p>
                ) : napRecord ? (
                  <div className="space-y-2.5 text-sm text-gray-700">
                    <div><span className="text-gray-400 text-xs">NAP Code:</span> <span className="font-semibold text-gray-900">{napRecord.nap_code}</span></div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><span className="text-gray-400 text-xs">Ports:</span> {napRecord.no_of_ports ?? "—"}</div>
                      <div><span className="text-gray-400 text-xs">Used Port:</span> {napRecord.used_port ?? "—"}</div>
                      <div><span className="text-gray-400 text-xs">Vacant:</span> {napRecord.vacant ?? "—"}</div>
                      <div><span className="text-gray-400 text-xs">Record:</span> {napRecord.stencil ?? "—"}</div>
                      <div><span className="text-gray-400 text-xs">ONU:</span> {napRecord.onu_connect ?? "—"}</div>
                    </div>
                    <div><span className="text-gray-400 text-xs">Area:</span> {napRecord.area || "—"}</div>
                    <div><span className="text-gray-400 text-xs">Lat/Lng:</span> {napRecord.latitude}, {napRecord.longitude}</div>
                    <div><span className="text-gray-400 text-xs">Updated By:</span> {napRecord.updated_by_name || "—"}</div>

                    {/* Action buttons */}
                    <div className="border-t pt-3 mt-3 flex flex-wrap gap-2">
                      {napRecord.qr_code_data && (
                        <button
                          onClick={() => setFstQrView({ qrData: napRecord.qr_code_data, napCode: napRecord.nap_code })}
                          className="px-3 py-1.5 text-xs bg-gray-800 text-white rounded-lg font-medium flex items-center gap-1"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h7v7H3V3zm11 0h7v7h-7V3zm-11 11h7v7H3v-7zm14 3h.01M17 17h.01M21 17h.01M14 14h3v3h-3v-3zm3 3h3v3h-3v-3z" />
                          </svg>
                          QR Code
                        </button>
                      )}
                      {hasPerm("nap_locator.view_on_ni_map") && (
                        <button
                          onClick={handleFstNapDetails}
                          disabled={fstFetchingNapDetails}
                          className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg font-medium flex items-center gap-1 disabled:opacity-50"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          {fstFetchingNapDetails ? "Fetching..." : "NAP Details"}
                        </button>
                      )}
                      {hasPerm("nap_locator.view_on_ni_map") && (
                        <button
                          onClick={handleFstViewOnTable}
                          className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg font-medium flex items-center gap-1"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 6h18M3 14h18M3 18h18" />
                          </svg>
                          View on Table
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 py-6 text-center">No details available</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Fullscreen QR Code overlay */}
      {fstQrView && (
        <div className="fixed inset-0 z-[3000] bg-white flex flex-col items-center justify-center" onClick={() => setFstQrView(null)}>
          <div className="absolute top-4 right-4">
            <button onClick={() => setFstQrView(null)} className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 text-gray-600 text-xl active:bg-gray-200">&times;</button>
          </div>
          <p className="mb-3 text-sm font-semibold text-gray-800">{fstQrView.napCode}</p>
          <img
            src={`data:image/png;base64,${fstQrView.qrData}`}
            alt="QR Code"
            className="w-[80vw] h-[80vw] max-w-[400px] max-h-[400px] object-contain"
          />
          <p className="mt-4 text-sm text-gray-500">Tap anywhere to close</p>
        </div>
      )}

      {/* NAP Details Result Modal */}
      {fstNapDetailsModal && (
        <NapDetailsResultModal
          data={fstNapDetailsModal}
          onClose={() => setFstNapDetailsModal(null)}
          onChanged={() => { setNapViewEnabled(false); setTimeout(() => setNapViewEnabled(true), 50); }}
        />
      )}

      {/* NAP Table Modal */}
      {fstNapTableModalSearch && (
        <NapTableModal
          key={fstNapTableModalSearch}
          lcpCode={fstNapTableModalSearch}
          onClose={() => setFstNapTableModalSearch(null)}
        />
      )}

      {/* Loading overlay when fetching NAP details */}
      {fstFetchingNapDetails && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/20">
          <div className="relative flex items-center justify-center w-20 h-20">
            <svg
              className="absolute inset-0 w-full h-full animate-spin"
              style={{ animationDuration: "1.5s" }}
              viewBox="0 0 100 100"
              fill="none"
            >
              <polygon
                points="50,2 93,25 93,75 50,98 7,75 7,25"
                stroke="#2563eb"
                strokeWidth="4"
                fill="none"
                strokeLinecap="round"
                strokeDasharray="12 6"
              />
            </svg>
            <img
              src="/best-icon.png"
              alt="Loading..."
              className="h-12 w-12"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function NIDashboardPage() {
  const { user, canAccessNIDashboard, isMobileRole, hasPerm, canAccessDashboard } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();

  // State
  const [stats, setStats] = useState(null);
  const [brgyData, setBrgyData] = useState([]);

  const [barangays, setBarangays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Filters
  const [filterMuni, setFilterMuni] = useState("");
  const [filterBrgy, setFilterBrgy] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  // Sub-stat filters (matching NI Table View behavior)
  const [pendingTeamFilter, setPendingTeamFilter] = useState(null);
  const [acSubFilter, setAcSubFilter] = useState(null);
  const [acCompanyFilter, setAcCompanyFilter] = useState(null);
  const [cancelReasonFilter, setCancelReasonFilter] = useState(null);
  const [reschedReasonFilter, setReschedReasonFilter] = useState(null);

  // Date range — restored from location.state when switching from table view
  const [dateFrom, setDateFrom] = useState(() => location.state?.dateFrom || getLocalToday());
  const [dateTo, setDateTo] = useState(() => location.state?.dateTo || getLocalToday());

  // Whether the selected date is today (used to conditionally show Rescheduled in map view)
  const isToday = dateFrom === getLocalToday();

  // Clear rescheduled filter when navigating away from today
  useEffect(() => {
    if (!isToday && filterStatus === "Rescheduled") {
      setFilterStatus("");
      setReschedReasonFilter(null);
    }
  }, [isToday, filterStatus]);

  // Search
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const searchTimer = useRef(null);

  // Right-side panel tab: "layers" | "subscriber" | "municipality"
  const [rightTab, setRightTab] = useState("noTeams");

  // Map focus
  const [flyTarget, setFlyTarget] = useState(null);
  const [flyZoom, setFlyZoom] = useState(null);
  const [focusNapPos, setFocusNapPos] = useState(null); // pulsating highlight for a NAP opened from a detail modal
  // Pan target (keeps current zoom) — set when a job order is clicked in the
  // Deployed Teams panel. Separate from flyTarget so zoom is never changed.
  const [panTarget, setPanTarget] = useState(null);

  // Subscriber location search (triggered from NI Table View click)
  const [subscriberPin, setSubscriberPin] = useState(null); // { lat, lng, name, address, joNumber }
  const [subscriberLocating, setSubscriberLocating] = useState(false);
  const [subscriberLocateMsg, setSubscriberLocateMsg] = useState(null); // { type: "success"|"warning"|"error", text }
  const [editingLocation, setEditingLocation] = useState(false);
  const [editLocConfirm, setEditLocConfirm] = useState(false);
  const [editLocSaving, setEditLocSaving] = useState(false);
  const pendingLocateRef = useRef(location.state?.locateSubscriber || null);

  // Individual map records (each JO with exact lat/lng)
  const [mapRecords, setMapRecords] = useState([]);

  // AbortController ref to cancel stale requests on date/filter change
  const mapAbortRef = useRef(null);

  // Teams
  const [dispatchTeams, setDispatchTeams] = useState([]);
  const [teamsSummary, setTeamsSummary] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);

  // Map layers
  const [showBasemap, setShowBasemap] = useState(true);
  const [showSatellite, setShowSatellite] = useState(false);
  const [showBrgyBoundaries, setShowBrgyBoundaries] = useState(false);
  const [showCityBoundaries, setShowCityBoundaries] = useState(true);
  const [showBrgyLabels, setShowBrgyLabels] = useState(true);
  const [showStreetLabels, setShowStreetLabels] = useState(true);
  const [showLandmarks, setShowLandmarks] = useState(false);
  const [showTeamLabels, setShowTeamLabels] = useState(true);
  const [showFSTLocations, setShowFSTLocations] = useState(false);
  const [showOtherCompanyTeams, setShowOtherCompanyTeams] = useState(false);

  // Inline editing state (reuses same components as NI table)
  const [teamMenu, setTeamMenu] = useState(null);         // { jo, rect }
  const [statusMenu, setStatusMenu] = useState(null);     // { jo, rect }
  const [directAction, setDirectAction] = useState(null); // { type, jo }
  const canEditTeam = hasPerm("field.edit.team_number");
  const canEditStatus = useMemo(() => {
    const a = [];
    if (hasPerm("jo.complete")) a.push("complete");
    if (hasPerm("jo.cancel")) a.push("cancel");
    if (hasPerm("jo.reschedule")) a.push("reschedule");
    return a;
  }, [hasPerm]);
  const [brgyGeoJSON, setBrgyGeoJSON] = useState(null);
  const [cityGeoJSON, setCityGeoJSON] = useState(null);

  // FST live locations (desktop only — polled every 15s)
  const [fstLocations, setFstLocations] = useState([]);

  // NAP overlay state (desktop only, permission-gated)
  const canViewNapOnNiMap = hasPerm("nap_locator.view_on_ni_map");
  const [napViewEnabled, setNapViewEnabled] = useState(canViewNapOnNiMap);
  const [selectedNap, setSelectedNap] = useState(null);
  const [niMapZoom, setNiMapZoom] = useState(SERVICE_AREA_ZOOM);
  const [napTableModalSearch, setNapTableModalSearch] = useState(null);

  // Measure tool state
  const [measureEnabled, setMeasureEnabled] = useState(false);
  const [measureTotal, setMeasureTotal] = useState(0); // LCP code to open table modal

  // Build centroid lookup from GeoJSON polygons
  const geoCentroids = useMemo(() => {
    const lookup = {};
    if (!brgyGeoJSON) return lookup;
    const CITY_TO_MUNI = {
      "city of pasig": "PASIG",
      "city of mandaluyong": "MANDALUYONG",
      "city of marikina": "MARIKINA",
      "city of san juan": "SAN JUAN",
    };
    const norm = (s) =>
      s.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
       .toUpperCase()
       .replace(/\(.*?\)/g, "")
       .replace(/[.\-]/g, " ")
       .replace(/\s+/g, " ")
       .trim();

    for (const f of brgyGeoJSON.features || []) {
      const rawBrgy = f.properties?.brgy_name || "";
      const city = (f.properties?.city_name || "").toLowerCase();
      const muni = CITY_TO_MUNI[city];
      if (!muni || !rawBrgy) continue;
      const center = L.geoJSON(f).getBounds().getCenter();
      const coords = [center.lat, center.lng];
      lookup[`${muni}|${rawBrgy.toUpperCase()}`] = coords;
      lookup[`${muni}|${norm(rawBrgy)}`] = coords;
    }

    const ALIASES = {
      "MARIKINA|STA ELENA": "MARIKINA|SANTA ELENA",
      "MARIKINA|STO NINO": "MARIKINA|SANTO NINO",
      "MARIKINA|CONCEPCION 2": "MARIKINA|CONCEPCION DOS",
      "MARIKINA|JESUS DELA PENA": "MARIKINA|JESUS DE LA PENA",
      "MARIKINA|PARAISO": "MARIKINA|FORTUNE",
      "PASIG|STA CRUZ": "PASIG|SANTA CRUZ",
      "PASIG|STA LUCIA": "PASIG|SANTA LUCIA",
      "PASIG|STO TOMAS": "PASIG|SANTO TOMAS",
      "PASIG|SAN NICOLAS": "PASIG|SAN NICOLAS",
      "PASIG|KATIPUNAN": "PASIG|BAGONG KATIPUNAN",
      "PASIG|NAPICO": "PASIG|PINAGBUHATAN",
      "PASIG|UGONG NORTE": "PASIG|UGONG",
      "MANDALUYONG|NEW ZANIGA": "MANDALUYONG|NEW ZANIGA",
      "MANDALUYONG|OLD ZANIGA": "MANDALUYONG|OLD ZANIGA",
      "MANDALUYONG|PAG ASA": "MANDALUYONG|PAG ASA",
      "MANDALUYONG|MABINI J RIZAL": "MANDALUYONG|MABINI J RIZAL",
      "MANDALUYONG|WACK WACK": "MANDALUYONG|WACK WACK GREENHILLS",
      "MANDALUYONG|WACK WACK GREENHILLS": "MANDALUYONG|WACK WACK GREENHILLS",
      "MANDALUYONG|HAGDANG BATO": "MANDALUYONG|HAGDANG BATO ITAAS",
      "MANDALUYONG|GREENHILLS": "MANDALUYONG|WACK WACK GREENHILLS",
      "SAN JUAN|STA LUCIA": "SAN JUAN|SANTA LUCIA",
      "SAN JUAN|BALONG BATO": "SAN JUAN|BALONG BATO",
      "SAN JUAN|ERMITANO": "SAN JUAN|ERMITANO",
      "SAN JUAN|PASADENA": "SAN JUAN|PASADENA",
      "SAN JUAN|SAN JOSE": "SAN JUAN|SAN PERFECTO",
    };

    for (const [dbKey, geoKey] of Object.entries(ALIASES)) {
      const normGeo = norm(geoKey.split("|")[1]);
      const muni = geoKey.split("|")[0];
      const resolved = lookup[`${muni}|${normGeo}`] || lookup[geoKey];
      if (resolved) lookup[dbKey] = resolved;
    }

    return lookup;
  }, [brgyGeoJSON]);

  // Resolve coords: prefer backend avg lat/lng, then GeoJSON centroid, then static
  const resolveCoords = useCallback(
    (municipality, barangay, avgLat, avgLng) => {
      // Use actual coordinates if available
      if (avgLat && avgLng && !isNaN(avgLat) && !isNaN(avgLng)) {
        return [avgLat, avgLng];
      }
      const muni = (municipality || "").toUpperCase();
      const brgy = (barangay || "").toUpperCase();
      const key = `${muni}|${brgy}`;
      if (geoCentroids[key]) return geoCentroids[key];
      const normKey = `${muni}|${brgy.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\(.*?\)/g, "").replace(/[.\-]/g, " ").replace(/\s+/g, " ").trim()}`;
      if (geoCentroids[normKey]) return geoCentroids[normKey];
      return getBarangayCoords(municipality, barangay);
    },
    [geoCentroids]
  );

  // Resolve coords for GeoJSON-only (no avg lat/lng)
  const resolveCoordsGeo = useCallback(
    (municipality, barangay) => {
      return resolveCoords(municipality, barangay, null, null);
    },
    [resolveCoords]
  );

  // Load boundary GeoJSON files
  useEffect(() => {
    fetch("/data/barangay_boundaries.geojson")
      .then((r) => r.json())
      .then(setBrgyGeoJSON)
      .catch(() => {});
    fetch("/data/city_boundaries.geojson")
      .then((r) => r.json())
      .then(setCityGeoJSON)
      .catch(() => {});
  }, []);

  // Poll FST live locations every 15 seconds (desktop/admin only)
  useEffect(() => {
    if (isMobileRole) return; // FST users see their own location via the hook
    let cancelled = false;
    const fetchLocations = () => {
      api.get("/fst/locations")
        .then((r) => { if (!cancelled) setFstLocations(r.data); })
        .catch(() => {});
    };
    fetchLocations();
    const timer = setInterval(fetchLocations, 15_000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [isMobileRole]);

  // Redirect if no permission
  useEffect(() => {
    if (!canAccessNIDashboard) navigate("/dashboard", { replace: true });
  }, [canAccessNIDashboard, navigate]);

  // Fetch stats + barangay data + municipalities
  const fetchData = useCallback(async () => {
    // Cancel any in-flight request to prevent stale responses
    if (mapAbortRef.current) mapAbortRef.current.abort();
    const controller = new AbortController();
    mapAbortRef.current = controller;

    setLoading(true);
    setError("");
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (filterMuni) params.municipality = filterMuni;

      // map-records fetched without municipality filter — filtering done client-side
      // so legend + municipality summary stay consistent from one dataset
      const dateParams = {};
      if (dateFrom) dateParams.date_from = dateFrom;
      if (dateTo) dateParams.date_to = dateTo;

      const [statsRes, brgyRes, teamsRes, teamsSumRes, mapRes] = await Promise.all([
        api.get("/ni/dashboard/stats", { params, signal: controller.signal }),
        api.get("/ni/dashboard/barangay-summary", { params, signal: controller.signal }),
        api.get("/ni/dashboard/dispatch-teams-today", { params, signal: controller.signal }),
        api.get("/ni/dashboard/dispatch-teams-today-summary", { params, signal: controller.signal }),
        api.get("/ni/dashboard/map-records", { params: dateParams, signal: controller.signal }),
      ]);
      setStats(statsRes.data);
      setBrgyData(brgyRes.data);
      setDispatchTeams(teamsRes.data);
      setTeamsSummary(teamsSumRes.data);
      setMapRecords(mapRes.data);
      setSelectedTeam(null);
    } catch (err) {
      // Ignore aborted requests — a newer request has already taken over
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
      setError("Failed to load NI dashboard data.");
      setStats(null);
      setBrgyData([]);
      setMapRecords([]);
      setDispatchTeams([]);
      setTeamsSummary([]);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, filterMuni]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Process pending subscriber location request (from NI Table View click)
  // Phase 1: Immediately use local barangay/city centroid for instant flyTo
  // Phase 2: Call geocoding API for precise street-level location (background)
  const subscriberLocateExecuted = useRef(false);

  useEffect(() => {
    if (loading) return; // map not rendered yet
    if (subscriberLocateExecuted.current) return; // already ran
    const req = pendingLocateRef.current;
    if (!req) return;
    subscriberLocateExecuted.current = true;
    pendingLocateRef.current = null;

    // Phase 1: Instant fly using existing coords or local barangay centroid
    const hasCoords = req.latitude && req.longitude && !isNaN(req.latitude) && !isNaN(req.longitude);
    let immediateCoords = null;

    if (hasCoords) {
      immediateCoords = [parseFloat(req.latitude), parseFloat(req.longitude)];
    } else {
      // Use local barangay/city centroid (instant, no API call)
      immediateCoords = getBarangayCoords(req.municipality, req.barangay);
    }

    if (immediateCoords) {
      const pin = {
        id: req.id || null,
        lat: immediateCoords[0],
        lng: immediateCoords[1],
        name: req.customerName,
        address: req.address,
        joNumber: req.joNumber,
        municipality: req.municipality,
        barangay: req.barangay,
      };
      setSubscriberPin(pin);
      setEditingLocation(true);
      setFlyTarget(immediateCoords);
      setFlyZoom(hasCoords ? 18 : 16);
      if (hasCoords && req.locationSaved) {
        setSubscriberLocateMsg({ type: "success", text: "Subscriber location found." });
        return; // saved location — no need for geocoding
      }
      if (hasCoords) {
        // Has coords but not explicitly saved — still skip geocoding (use existing coords)
        setSubscriberLocateMsg({ type: "success", text: "Subscriber location found." });
        return;
      }
      setSubscriberLocating(true);
      setSubscriberLocateMsg({ type: "warning", text: "Showing barangay area. Searching for exact address…" });
    } else {
      setSubscriberLocating(true);
      setSubscriberLocateMsg(null);
    }

    // Phase 2: Call geocoding API for street-level precision (background)
    const refineLocation = async () => {
      try {
        const result = await geocodeSubscriberAddress({
          address: req.address,
          barangay: req.barangay,
          municipality: req.municipality,
          latitude: null, // force geocoding even if we have centroid
          longitude: null,
        });

        if (result.success && result.latitude && result.longitude) {
          const pin = {
            id: req.id || null,
            lat: result.latitude,
            lng: result.longitude,
            name: req.customerName,
            address: req.address,
            joNumber: req.joNumber,
            municipality: req.municipality,
            barangay: req.barangay,
          };
          setSubscriberPin(pin);
          setEditingLocation(true);
          setFlyTarget([result.latitude, result.longitude]);
          setFlyZoom(18);
          const msgType = (result.accuracy === "exact" || result.accuracy === "address") ? "success" : "warning";
          setSubscriberLocateMsg({ type: msgType, text: result.message });
        } else if (!immediateCoords) {
          // No local coords and geocoding failed
          setSubscriberLocateMsg({ type: "error", text: result.message });
        } else {
          // Have local coords but geocoding couldn't refine
          setSubscriberLocateMsg({ type: "warning", text: "Exact address not found. Showing barangay area." });
        }
      } catch {
        if (!immediateCoords) {
          setSubscriberLocateMsg({ type: "error", text: "Unable to locate subscriber address." });
        } else {
          setSubscriberLocateMsg({ type: "warning", text: "Exact address not found. Showing barangay area." });
        }
      } finally {
        setSubscriberLocating(false);
      }
    };

    refineLocation();
  }, [loading]);

  // Focus a specific NAP when navigated from a Job Order detail modal:
  // turn on the NAP overlay and fly to the NAP location (zoom 18).
  const pendingFocusNapRef = useRef(location.state?.focusNap || null);
  const focusNapExecuted = useRef(false);
  useEffect(() => {
    if (loading) return;
    if (focusNapExecuted.current) return;
    const f = pendingFocusNapRef.current;
    if (!f) return;
    focusNapExecuted.current = true;
    pendingFocusNapRef.current = null;
    const lat = parseFloat(f.latitude), lng = parseFloat(f.longitude);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;
    if (canViewNapOnNiMap) setNapViewEnabled(true);
    setFocusNapPos([lat, lng]);
    setFlyTarget([lat, lng]);
    setFlyZoom(18);
  }, [loading]);

  // Auto-dismiss subscriber locate message after 5 seconds
  useEffect(() => {
    if (!subscriberLocateMsg) return;
    const t = setTimeout(() => setSubscriberLocateMsg(null), 5000);
    return () => clearTimeout(t);
  }, [subscriberLocateMsg]);

  // Fetch barangays when municipality filter changes
  useEffect(() => {
    if (!filterMuni) {
      setBarangays([]);
      setFilterBrgy("");
      return;
    }
    api
      .get("/ni/dashboard/barangays", { params: { municipality: filterMuni } })
      .then((r) => setBarangays(r.data))
      .catch(() => setBarangays([]));
    setFilterBrgy("");
  }, [filterMuni]);

  // Debounced search
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const term = searchTerm.trim();
    if (term.length < 2) {
      setSearchResults([]);
      return;
    }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/ni/dashboard/search", {
          params: { q: term },
        });
        setSearchResults(data);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(searchTimer.current);
  }, [searchTerm]);

  // Select a search result
  const handleSelectResult = (rec) => {
    setSelectedRecord(rec);
    setSelectedNap(null);
    setRightTab("subscriber");
    setSearchResults([]);
    setSearchTerm(rec.jo_number || rec.account_number || "");

    const hasCoords = rec.latitude && rec.longitude && !isNaN(rec.latitude) && !isNaN(rec.longitude);

    // If location was previously saved, fly directly — no geocoding needed
    if (rec.location_saved && hasCoords) {
      const pin = {
        id: rec.id,
        lat: parseFloat(rec.latitude),
        lng: parseFloat(rec.longitude),
        name: rec.name || rec.customer_full_name,
        address: rec.complete_address || `${rec.barangay}, ${rec.municipality}`,
        joNumber: rec.jo_number,
        municipality: rec.municipality,
        barangay: rec.barangay,
      };
      setSubscriberPin(pin);
      setEditingLocation(true);
      setFlyTarget([pin.lat, pin.lng]);
      setFlyZoom(18);
      setSubscriberLocateMsg({ type: "success", text: "Subscriber location found." });
      return;
    }

    // No saved location — geocode for precise position
    if (hasCoords) {
      const pin = {
        id: rec.id,
        lat: parseFloat(rec.latitude),
        lng: parseFloat(rec.longitude),
        name: rec.name || rec.customer_full_name,
        address: rec.complete_address || `${rec.barangay}, ${rec.municipality}`,
        joNumber: rec.jo_number,
        municipality: rec.municipality,
        barangay: rec.barangay,
      };
      setSubscriberPin(pin);
      setEditingLocation(true);
      setFlyTarget([pin.lat, pin.lng]);
      setFlyZoom(18);
    } else {
      const coords = resolveCoordsGeo(rec.municipality, rec.barangay);
      if (coords) {
        const pin = {
          id: rec.id,
          lat: coords[0],
          lng: coords[1],
          name: rec.name || rec.customer_full_name,
          address: rec.complete_address || `${rec.barangay}, ${rec.municipality}`,
          joNumber: rec.jo_number,
          municipality: rec.municipality,
          barangay: rec.barangay,
        };
        setSubscriberPin(pin);
        setEditingLocation(true);
        setFlyTarget([...coords]);
        setFlyZoom(16);
      }
    }
  };

  // Pan the map to a record's location WITHOUT changing the current zoom.
  // Prefers exact coords, falls back to the barangay centroid, else a toast.
  const panToRecord = useCallback((rec) => {
    const hasCoords =
      rec.latitude != null && rec.longitude != null &&
      !isNaN(rec.latitude) && !isNaN(rec.longitude);

    if (hasCoords) {
      setPanTarget({ lat: parseFloat(rec.latitude), lng: parseFloat(rec.longitude), nonce: Date.now() });
    } else {
      const coords = resolveCoordsGeo(rec.municipality, rec.barangay);
      if (coords) {
        setPanTarget({ lat: coords[0], lng: coords[1], nonce: Date.now() });
        setSubscriberLocateMsg({ type: "warning", text: "No exact coordinates — showing barangay area." });
      } else {
        setSubscriberLocateMsg({ type: "error", text: "No valid coordinates for this job order." });
      }
    }
  }, [resolveCoordsGeo]);

  // Job order clicked from the Deployed Teams panel.
  // Reuses the same selection logic as a marker click (set record + open the
  // Subscriber tab) and pans the map to the location WITHOUT changing zoom.
  const handleJobOrderClick = useCallback((rec) => {
    setSelectedRecord(rec);
    setSelectedNap(null);
    setRightTab("subscriber");
    panToRecord(rec);
  }, [panToRecord]);

  // Card clicked from the No Teams tab — highlight + pan, but stay on the tab
  // so the user can keep assigning teams without losing their place.
  const handleNoTeamCardClick = useCallback((rec) => {
    setSelectedRecord(rec);
    setSelectedNap(null);
    panToRecord(rec);
  }, [panToRecord]);

  // Locate subscriber by address (geocode) — triggered from marker name click
  const handleLocateByAddress = useCallback(async (record) => {
    if (!record) return;
    const address = record.complete_address || record.address || "";

    const hasCoords = record.latitude && record.longitude && !isNaN(record.latitude) && !isNaN(record.longitude);

    // If we already have a recorded lat/lon (saved or not), fly straight to it —
    // no geocoding. Mirrors the Table View: always land on the last recorded
    // location and let the user update the pin from there.
    if (hasCoords) {
      const pin = {
        id: record.id || null,
        lat: parseFloat(record.latitude),
        lng: parseFloat(record.longitude),
        name: record.name || record.customer_full_name,
        address: address,
        joNumber: record.jo_number,
        municipality: record.municipality,
        barangay: record.barangay,
      };
      setSubscriberPin(pin);
      setEditingLocation(true);
      setEditLocConfirm(false);
      setFlyTarget([pin.lat, pin.lng]);
      setFlyZoom(18);
      setSubscriberLocateMsg({ type: "success", text: "Subscriber location found." });
      return;
    }

    // No recorded coords — Phase 1: instant fly to barangay centroid if available
    const immediateCoords = resolveCoordsGeo(record.municipality, record.barangay);

    const pinBase = {
      id: record.id || null,
      name: record.name || record.customer_full_name,
      address: address,
      joNumber: record.jo_number,
      municipality: record.municipality,
      barangay: record.barangay,
    };

    if (immediateCoords) {
      setSubscriberPin({ ...pinBase, lat: immediateCoords[0], lng: immediateCoords[1] });
      setFlyTarget(immediateCoords);
      setFlyZoom(16);
    }

    setSubscriberLocating(true);
    setSubscriberLocateMsg({ type: "warning", text: "Searching for address-based location…" });

    // Phase 2: Geocode the address for precise location
    try {
      const result = await geocodeSubscriberAddress({
        address: address,
        barangay: record.barangay,
        municipality: record.municipality,
        latitude: null,
        longitude: null,
      });

      if (result.success && result.latitude && result.longitude) {
        setSubscriberPin({ ...pinBase, lat: result.latitude, lng: result.longitude });
        setFlyTarget([result.latitude, result.longitude]);
        setFlyZoom(18);
        setEditingLocation(true);
        setEditLocConfirm(false);
        const msgType = (result.accuracy === "exact" || result.accuracy === "address") ? "success" : "warning";
        setSubscriberLocateMsg({ type: msgType, text: result.message });
      } else if (immediateCoords) {
        setEditingLocation(true);
        setEditLocConfirm(false);
        setSubscriberLocateMsg({ type: "warning", text: "Exact address not found. Showing barangay area." });
      } else {
        setSubscriberLocateMsg({ type: "error", text: "Unable to locate subscriber address." });
      }
    } catch {
      if (immediateCoords) {
        setEditingLocation(true);
        setEditLocConfirm(false);
        setSubscriberLocateMsg({ type: "warning", text: "Exact address not found. Showing barangay area." });
      } else {
        setSubscriberLocateMsg({ type: "error", text: "Unable to locate subscriber address." });
      }
    } finally {
      setSubscriberLocating(false);
    }
  }, [resolveCoordsGeo]);

  // Subscriber/customer NAME clicked from a panel card (Deployed Teams / No Teams).
  // Mirrors the table & map-marker name click: select the record and run the
  // locate-by-address flow (fly to saved flag, or geocode + drop an editable pin
  // with all the relocate/save controls).
  const handleSubscriberNameClick = useCallback((record) => {
    setSelectedRecord(record);
    setSelectedNap(null);
    handleLocateByAddress(record);
  }, [handleLocateByAddress]);

  // Navigate date
  const navigateDate = (days) => {
    const base = dateFrom ? new Date(dateFrom + "T00:00:00") : new Date();
    base.setDate(base.getDate() + days);
    const y = base.getFullYear();
    const m = String(base.getMonth() + 1).padStart(2, "0");
    const d = String(base.getDate()).padStart(2, "0");
    const newDate = `${y}-${m}-${d}`;
    setDateFrom(newDate);
    setDateTo(newDate);
  };

  const setToday = () => {
    const today = getLocalToday();
    setDateFrom(today);
    setDateTo(today);
  };

  // --- Inline editing handlers (same pattern as NI table) ---

  const handleTeamClick = useCallback((jo, e) => {
    // Block team edit if locked by another company
    if (jo.team_assigned_by_company && jo.team_assigned_by_company !== user?.company_id) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setTeamMenu({ jo, rect });
  }, [user?.company_id]);

  const handleTeamSave = useCallback((updatedRecord) => {
    setTeamMenu(null);
    // Merge updated fields into selectedRecord so the panel refreshes immediately
    if (updatedRecord) {
      // Parse personnel display names from the response
      const pNames = updatedRecord.personnel_names || "";
      const parts = pNames.split(",").map((s) => s.trim());
      setSelectedRecord((prev) =>
        prev && prev.id === updatedRecord.id
          ? {
              ...prev,
              team_number: updatedRecord.team_number,
              team_assigned_by_company: updatedRecord.team_assigned_by_company,
              personnel_1_id: updatedRecord.personnel_1_id,
              personnel_2_id: updatedRecord.personnel_2_id,
              personnel_names: updatedRecord.personnel_names,
              personnel_1_name: parts[0] || null,
              personnel_2_name: parts[1] || null,
            }
          : prev
      );
      // Also update in mapRecords so the map stays in sync
      setMapRecords((prev) =>
        prev.map((r) =>
          r.id === updatedRecord.id
            ? {
                ...r,
                team_number: updatedRecord.team_number,
                team_assigned_by_company: updatedRecord.team_assigned_by_company,
                personnel_1_id: updatedRecord.personnel_1_id,
                personnel_2_id: updatedRecord.personnel_2_id,
                personnel_names: updatedRecord.personnel_names,
                personnel_1_name: parts[0] || null,
                personnel_2_name: parts[1] || null,
              }
            : r
        )
      );
    }
    fetchData();
  }, [fetchData]);

  const handleStatusClick = useCallback((jo, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setStatusMenu({ jo, rect });
  }, []);

  const handleStatusAction = useCallback((action) => {
    if (!statusMenu) return;
    setDirectAction({ type: action, jo: statusMenu.jo });
    setStatusMenu(null);
  }, [statusMenu]);

  const handleDirectActionDone = useCallback(() => {
    setDirectAction(null);
    setSelectedRecord(null);
    fetchData();
  }, [fetchData]);

  // Build team color map
  const teamColorMap = useMemo(() => {
    const map = {};
    const allTeams = new Set();
    dispatchTeams.forEach((loc) => loc.teams.forEach((t) => allTeams.add(t.team_number)));
    teamsSummary.forEach((t) => allTeams.add(t.team_number));
    [...allTeams].sort().forEach((team, i) => {
      map[team] = TEAM_COLORS[i % TEAM_COLORS.length];
    });
    return map;
  }, [dispatchTeams, teamsSummary]);

  // Filtered dispatch markers based on selected team and company
  const filteredDispatchTeams = useMemo(() => {
    let data = dispatchTeams;
    if (!showOtherCompanyTeams && user?.company_id) {
      data = data.map((loc) => ({
        ...loc,
        teams: loc.teams.filter((t) => !t.company_id || t.company_id === user.company_id),
      })).filter((loc) => loc.teams.length > 0);
    }
    if (selectedTeam) {
      data = data
        .map((loc) => ({
          ...loc,
          teams: loc.teams.filter((t) => t.team_number === selectedTeam),
        }))
        .filter((loc) => loc.teams.length > 0);
    }
    return data;
  }, [dispatchTeams, selectedTeam, showOtherCompanyTeams, user?.company_id]);

  const filteredTeamsSummary = useMemo(() => {
    if (showOtherCompanyTeams || !user?.company_id) return teamsSummary;
    return teamsSummary.filter((t) => !t.company_id || t.company_id === user.company_id);
  }, [teamsSummary, showOtherCompanyTeams, user?.company_id]);

  const selectedTeamBrgys = useMemo(() => {
    if (!selectedTeam) return null;
    const s = new Set();
    dispatchTeams.forEach((loc) => {
      if (loc.teams.some((t) => t.team_number === selectedTeam)) {
        s.add(`${(loc.municipality || "").toUpperCase()}|${(loc.barangay || "").toUpperCase()}`);
      }
    });
    return s;
  }, [selectedTeam, dispatchTeams]);

  // Filter barangay data for map
  const filteredBrgyData = useMemo(() => {
    let data = brgyData;
    if (filterBrgy) {
      data = data.filter(
        (b) => b.barangay?.toUpperCase() === filterBrgy.toUpperCase()
      );
    }
    if (filterStatus) {
      const key = filterStatus === "Active/Complete" ? "completed" : filterStatus.toLowerCase().replace(/ /g, "_");
      data = data.filter((b) => (b[key] || 0) > 0);
    }
    if (selectedTeamBrgys) {
      data = data.filter((b) =>
        selectedTeamBrgys.has(
          `${(b.municipality || "").toUpperCase()}|${(b.barangay || "").toUpperCase()}`
        )
      );
    }
    return data;
  }, [brgyData, filterBrgy, filterStatus, selectedTeamBrgys]);

  // Helper: team presence check
  const _hasTeam = useCallback((tn) => tn && tn.trim() && tn.trim() !== "-", []);
  const _isBest = useCallback((tn) => { const u = (tn || "").trim().toUpperCase(); return u.startsWith("BEST") || u.startsWith("BST"); }, []);
  const _isA6 = useCallback((tn) => { const u = (tn || "").trim().toUpperCase(); return u.startsWith("ATS") || u.startsWith("A6"); }, []);

  // Helper: reset all sub-stat filters
  const resetSubFilters = useCallback(() => {
    setPendingTeamFilter(null);
    setAcSubFilter(null);
    setAcCompanyFilter(null);
    setCancelReasonFilter(null);
    setReschedReasonFilter(null);
  }, []);

  // Date change handler for DateFilter component (desktop, matches NI Table behavior)
  const handleDateChange = useCallback((val) => {
    setDateFrom(val.from || "");
    setDateTo(val.to || "");
    // View All resets status filters (same as original behavior)
    if (!val.from && !val.to) {
      setFilterStatus("");
      setPendingTeamFilter(null);
      setAcSubFilter(null);
      setAcCompanyFilter(null);
      setCancelReasonFilter(null);
      setReschedReasonFilter(null);
    }
  }, []);

  // Teams count — derived from teamsSummary (matches NI Table top bar)
  const teamsCount = useMemo(() => filteredTeamsSummary.length, [filteredTeamsSummary]);

  // Filter individual map records
  const filteredMapRecords = useMemo(() => {
    let data = mapRecords;
    if (filterBrgy) {
      data = data.filter(
        (r) => (r.barangay || "").toUpperCase() === filterBrgy.toUpperCase()
      );
    }
    if (filterMuni) {
      data = data.filter(
        (r) => (r.municipality || "").toUpperCase() === filterMuni.toUpperCase()
      );
    }
    if (filterStatus) {
      data = data.filter((r) => r.status === filterStatus);
    }
    // Sub-stat filters
    if (filterStatus === "Pending" && pendingTeamFilter) {
      data = data.filter((r) =>
        pendingTeamFilter === "with_team" ? _hasTeam(r.team_number) : !_hasTeam(r.team_number)
      );
    }
    if (filterStatus === "Active/Complete" && acSubFilter) {
      data = data.filter((r) => {
        if (acSubFilter === "gofiber_only") return r.gofiber_active && r.jo_status !== "Completed";
        if (acSubFilter === "jo_only") return !r.gofiber_active && r.jo_status === "Completed";
        if (acSubFilter === "both") return r.gofiber_active && r.jo_status === "Completed";
        return true;
      });
    }
    if (filterStatus === "Active/Complete" && acCompanyFilter) {
      data = data.filter((r) => {
        if (acCompanyFilter === "best") return _isBest(r.team_number);
        if (acCompanyFilter === "a6") return _isA6(r.team_number);
        if (acCompanyFilter === "unknown") return !_isBest(r.team_number) && !_isA6(r.team_number);
        return true;
      });
    }
    if (filterStatus === "Cancelled" && cancelReasonFilter) {
      data = data.filter((r) => (r.cancel_reasons || []).includes(cancelReasonFilter));
    }
    if (filterStatus === "Rescheduled" && reschedReasonFilter) {
      data = data.filter((r) => (r.resched_reasons || []).includes(reschedReasonFilter));
    }
    // Hide Rescheduled from desktop map view when selected date is not today
    if (!isToday) {
      data = data.filter((r) => r.status !== "Rescheduled");
    }
    if (selectedTeam) {
      data = data.filter((r) => r.team_number === selectedTeam);
    }
    return data;
  }, [mapRecords, filterBrgy, filterMuni, filterStatus, selectedTeam, pendingTeamFilter, acSubFilter, acCompanyFilter, cancelReasonFilter, reschedReasonFilter, _hasTeam, _isBest, _isA6, isToday]);

  // Base-filtered records (all filters except status/sub-stat) — used for legend & stat counts
  const baseFilteredRecords = useMemo(() => {
    let data = mapRecords;
    // Hide Rescheduled from desktop map view when selected date is not today
    if (!isToday) {
      data = data.filter((r) => r.status !== "Rescheduled");
    }
    if (filterMuni) {
      data = data.filter(
        (r) => (r.municipality || "").toUpperCase() === filterMuni.toUpperCase()
      );
    }
    if (filterBrgy) {
      data = data.filter(
        (r) => (r.barangay || "").toUpperCase() === filterBrgy.toUpperCase()
      );
    }
    if (selectedTeam) {
      data = data.filter((r) => r.team_number === selectedTeam);
    }
    return data;
  }, [mapRecords, filterMuni, filterBrgy, selectedTeam, isToday]);

  // Records scoped to the user's company for the Deployed Teams panel.
  // By default only own-company teams are shown; the panel's "include other
  // company" checkbox toggles `showOtherCompanyTeams` (shared with the map
  // markers / Teams Today legend). `team_company_id` is the team's authoritative
  // owner (from team_masters), not who assigned it. A record whose team is
  // unknown to team_masters (no team_company_id) is treated as own.
  // Records for the Deployed Teams panel. Mirrors baseFilteredRecords, but
  // intentionally KEEPS Rescheduled history records on past dates: the live
  // records only survive as C and X once a date is past, so
  // the panel needs the dated history snapshots to reconstruct that day's P
  // (auto-rolled Pending) and R (manual reschedule). Map markers + legend keep
  // using baseFilteredRecords, so their past-date behavior is unchanged.
  const panelBaseRecords = useMemo(() => {
    let data = mapRecords;
    if (filterMuni) {
      data = data.filter(
        (r) => (r.municipality || "").toUpperCase() === filterMuni.toUpperCase()
      );
    }
    if (filterBrgy) {
      data = data.filter(
        (r) => (r.barangay || "").toUpperCase() === filterBrgy.toUpperCase()
      );
    }
    if (selectedTeam) {
      data = data.filter((r) => r.team_number === selectedTeam);
    }
    return data;
  }, [mapRecords, filterMuni, filterBrgy, selectedTeam]);

  const companyScopedPanelRecords = useMemo(() => {
    if (showOtherCompanyTeams || !user?.company_id) return panelBaseRecords;
    return panelBaseRecords.filter(
      (r) => !r.team_company_id || r.team_company_id === user.company_id
    );
  }, [panelBaseRecords, showOtherCompanyTeams, user?.company_id]);

  // Records with no team assigned yet — source for the "No Teams" tab.
  // Scoped to the user's own company: only JOs tagged to that company are
  // shown — JOs with no assigned company or tagged to another company are
  // excluded. Respects the active municipality filter (map-records are
  // fetched without it).
  const noTeamRecords = useMemo(() => {
    let data = mapRecords.filter((r) => !_hasTeam(r.team_number));
    if (user?.company_id) {
      data = data.filter((r) => r.company_id === user.company_id);
    }
    if (filterMuni) {
      data = data.filter((r) => (r.municipality || "").toUpperCase() === filterMuni.toUpperCase());
    }
    return data.sort((a, b) =>
      `${a.barangay || ""}${a.name || ""}`.localeCompare(`${b.barangay || ""}${b.name || ""}`)
    );
  }, [mapRecords, filterMuni, _hasTeam, user?.company_id]);

  // Derive legend counts from mapRecords (single source of truth)
  const legendCounts = useMemo(() => {
    const counts = {};
    for (const r of baseFilteredRecords) {
      counts[r.status] = (counts[r.status] || 0) + 1;
    }
    return counts;
  }, [baseFilteredRecords]);

  // Compute sub-stat counts from baseFilteredRecords (same source of truth as stats)
  const mapSubStats = useMemo(() => {
    const pending = baseFilteredRecords.filter((r) => r.status === "Pending");
    const ac = baseFilteredRecords.filter((r) => r.status === "Active/Complete");
    const cancelled = baseFilteredRecords.filter((r) => r.status === "Cancelled");
    const rescheduled = baseFilteredRecords.filter((r) => r.status === "Rescheduled");

    // Pending breakdown
    const pendingWithTeam = pending.filter((r) => _hasTeam(r.team_number)).length;
    const pendingWithoutTeam = pending.length - pendingWithTeam;

    // Active/Complete breakdown
    const acGfOnly = ac.filter((r) => r.gofiber_active && r.jo_status !== "Completed").length;
    const acJoOnly = ac.filter((r) => !r.gofiber_active && r.jo_status === "Completed").length;
    const acBoth = ac.filter((r) => r.gofiber_active && r.jo_status === "Completed").length;
    const acBest = ac.filter((r) => _isBest(r.team_number)).length;
    const acA6 = ac.filter((r) => _isA6(r.team_number)).length;
    const acUnknown = ac.length - acBest - acA6;

    // Cancel reason breakdown
    const cancelReasonCounts = {};
    for (const r of cancelled) {
      for (const reason of (r.cancel_reasons || [])) {
        cancelReasonCounts[reason] = (cancelReasonCounts[reason] || 0) + 1;
      }
    }

    // Resched reason breakdown
    const reschedReasonCounts = {};
    for (const r of rescheduled) {
      for (const reason of (r.resched_reasons || [])) {
        reschedReasonCounts[reason] = (reschedReasonCounts[reason] || 0) + 1;
      }
    }

    return {
      pendingWithTeam, pendingWithoutTeam,
      acGfOnly, acJoOnly, acBoth, acBest, acA6, acUnknown,
      cancelReasonCounts, reschedReasonCounts,
    };
  }, [baseFilteredRecords, _hasTeam, _isBest, _isA6]);

  // Municipality counts for dropdown (unfiltered by municipality/stat — from all mapRecords)
  const allMuniCounts = useMemo(() => {
    const counts = {};
    for (const r of mapRecords) {
      const name = (r.municipality || "").toUpperCase();
      if (!name) continue;
      counts[name] = (counts[name] || 0) + 1;
    }
    return Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [mapRecords]);

  // Derive per-barangay stats from filteredMapRecords (reacts to stat/sub-stat filters)
  const filteredBrgyStats = useMemo(() => {
    const map = {};
    for (const r of filteredMapRecords) {
      const key = `${(r.municipality || "").toUpperCase()}|${(r.barangay || "").toUpperCase()}`;
      if (!map[key]) map[key] = { municipality: (r.municipality || "").toUpperCase(), barangay: (r.barangay || "").toUpperCase(), total: 0, pending: 0, completed: 0, cancelled: 0, rescheduled: 0 };
      map[key].total++;
      if (r.status === "Pending") map[key].pending++;
      else if (r.status === "Active/Complete") map[key].completed++;
      else if (r.status === "Cancelled") map[key].cancelled++;
      else if (r.status === "Rescheduled") map[key].rescheduled++;
    }
    return Object.values(map);
  }, [filteredMapRecords]);

  // Top barangays by pending (derived from filtered data)
  const topPendingBrgys = useMemo(() => {
    return [...filteredBrgyStats]
      .map((b) => ({
        ...b,
        actionable: b.pending || 0,
      }))
      .filter((b) => b.actionable > 0)
      .sort((a, b) => b.actionable - a.actionable)
      .slice(0, 10);
  }, [filteredBrgyStats]);

  if (!canAccessNIDashboard) return null;

  // FST / mobile-only users get simplified dashboard
  if (isMobileRole) return <FSTDashboard />;

  return (
    <div className="min-h-screen lg:h-screen bg-gray-100 flex flex-col lg:overflow-hidden">
      {isMobile ? <MobileNavbar /> : <Navbar />}
      {/* Module accent strip */}
      {!isMobile && <div className={`h-1 ${NI.strip} flex-shrink-0`} />}

      <main className="flex-1 flex flex-col min-h-0 px-4 lg:px-6 pt-3 pb-2 w-full relative">
        {/* View toggle — anchored top-right for consistent positioning across Table/Map views */}
        {canAccessDashboard && (
          <div
            className="absolute top-3 right-4 lg:right-6 z-20 flex rounded-lg border border-gray-300 overflow-hidden cursor-pointer"
            onClick={() => navigate("/dashboard", { state: { dateFrom, dateTo } })}
          >
            <span className="px-3 py-1.5 text-sm font-medium bg-white text-gray-600 flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 6h18M3 14h18M3 18h18" /></svg>
              Table
            </span>
            <span className={`px-3 py-1.5 text-sm font-medium ${NI.btnPrimaryStatic} flex items-center gap-1`}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg>
              Map
            </span>
          </div>
        )}

        {/* Toolbar — identical structure to NI Table view (DashboardPage) */}
        <div className={`flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3 flex-shrink-0${canAccessDashboard ? " md:pr-[180px]" : ""}`}>
          <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
            {/* Search — with map-specific autocomplete */}
            <div className="relative">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setSelectedRecord(null);
                  }}
                  placeholder="Search JO#, name, account…"
                  className="border rounded px-3 py-1.5 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => { setSearchTerm(""); setSearchResults([]); setSelectedRecord(null); }}
                    className="text-sm text-gray-500 hover:text-gray-800"
                  >
                    Clear
                  </button>
                )}
              </div>
              {searching && (
                <div className="absolute right-3 top-2 text-xs text-gray-400">…</div>
              )}
              {searchResults.length > 0 && (
                <div className="absolute z-50 left-0 right-0 mt-1 bg-white border rounded shadow-lg max-h-64 overflow-y-auto">
                  {searchResults.map((r) => (
                    <button
                      key={r.id}
                      onClick={() => handleSelectResult(r)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b last:border-b-0"
                    >
                      <span className="font-medium text-gray-800">
                        {r.jo_number || r.account_number}
                      </span>
                      <span className="text-gray-500 ml-2">{r.name}</span>
                      <span className="text-gray-400 ml-2 text-xs">
                        {r.barangay}, {r.municipality}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <DateFilter value={{ from: dateFrom, to: dateTo }} onChange={handleDateChange} accent="teal" showViewAll={false} />
          </div>
          <div className="flex gap-2 items-center justify-end flex-wrap">
            <div className="inline-flex items-center gap-2 rounded border border-teal-300 bg-teal-100 px-3 py-1.5 select-none whitespace-nowrap leading-none">
              <span className="text-xs font-semibold uppercase tracking-wide text-teal-800">Teams</span>
              <span className="text-sm font-bold text-teal-900 tabular-nums">{teamsCount}</span>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded px-4 py-3 mb-3">
            {error}
          </div>
        )}

        {/* Stats — identical structure to NI Table view (DashboardPage) */}
        <div className="flex items-stretch gap-2 mb-3 flex-shrink-0">
          <div className={`flex-shrink-0 ${NI.badge} rounded-lg shadow-sm hidden sm:flex items-center gap-2 px-4 py-2`}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d={NI.iconPath} /></svg>
            <span className="text-xs font-bold uppercase tracking-wider">New Installation</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 flex-1">
          {/* Pending card */}
          {(() => {
            const isPendingActive = filterStatus === "Pending" && !pendingTeamFilter;
            const isWithTeamActive = filterStatus === "Pending" && pendingTeamFilter === "with_team";
            const isWithoutTeamActive = filterStatus === "Pending" && pendingTeamFilter === "without_team";
            const pendingCount = legendCounts.Pending || 0;
            const { pendingWithTeam, pendingWithoutTeam } = mapSubStats;
            const handlePendingClick = () => {
              resetSubFilters();
              if (isPendingActive) setFilterStatus("");
              else setFilterStatus("Pending");
            };
            const handleTeamClick = (type) => (e) => {
              e.stopPropagation();
              setAcSubFilter(null); setAcCompanyFilter(null);
              setCancelReasonFilter(null); setReschedReasonFilter(null);
              if (pendingTeamFilter === type) { setFilterStatus(""); setPendingTeamFilter(null); }
              else { setFilterStatus("Pending"); setPendingTeamFilter(type); }
            };
            return (
              <div
                className={`rounded-lg px-3 py-2 text-center border bg-yellow-100 text-yellow-800 transition-all hover:shadow cursor-pointer ${
                  (isPendingActive || isWithTeamActive || isWithoutTeamActive) ? 'ring-2 ring-blue-600 border-blue-600 shadow-md' : ''
                }`}
                style={{ opacity: pendingCount === 0 && !loading ? 0.5 : 1 }}
              >
                <div onClick={pendingCount > 0 ? handlePendingClick : undefined} title="Show only Pending job orders"
                  className={`cursor-pointer ${isPendingActive ? 'font-black' : ''}`}>
                  <div className="flex items-baseline justify-center gap-1.5 leading-tight">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-700">Pending</span>
                    <span className="text-lg font-bold text-gray-900 tabular-nums">{loading ? "..." : pendingCount}</span>
                  </div>
                </div>
                <div className="hidden sm:flex items-center justify-center gap-1.5 mt-0.5 border-t border-yellow-300 pt-1">
                  <button onClick={handleTeamClick("with_team")} disabled={pendingWithTeam === 0}
                    className={`text-[10px] leading-tight px-1.5 py-0.5 rounded transition-all ${
                      isWithTeamActive ? 'bg-yellow-500 text-white font-bold shadow-sm' : 'hover:bg-yellow-200 text-yellow-900'
                    } ${pendingWithTeam === 0 ? 'opacity-40 cursor-default' : 'cursor-pointer'}`}
                    title="Show pending with team assigned"
                  >
                    <span className="font-semibold">{pendingWithTeam}</span> w/ Team
                  </button>
                  <span className="text-yellow-400 text-[10px]">|</span>
                  <button onClick={handleTeamClick("without_team")} disabled={pendingWithoutTeam === 0}
                    className={`text-[10px] leading-tight px-1.5 py-0.5 rounded transition-all ${
                      isWithoutTeamActive ? 'bg-yellow-500 text-white font-bold shadow-sm' : 'hover:bg-yellow-200 text-yellow-900'
                    } ${pendingWithoutTeam === 0 ? 'opacity-40 cursor-default' : 'cursor-pointer'}`}
                    title="Show pending without team assigned"
                  >
                    <span className="font-semibold">{pendingWithoutTeam}</span> w/o Team
                  </button>
                </div>
              </div>
            );
          })()}
          {/* Active/Complete card */}
          {(() => {
            const isAcActive = filterStatus === "Active/Complete" && !acSubFilter && !acCompanyFilter;
            const isGfActive = filterStatus === "Active/Complete" && acSubFilter === "gofiber_only";
            const isJoActive = filterStatus === "Active/Complete" && acSubFilter === "jo_only";
            const isBothActive = filterStatus === "Active/Complete" && acSubFilter === "both";
            const isBestActive = filterStatus === "Active/Complete" && acCompanyFilter === "best";
            const isA6Active = filterStatus === "Active/Complete" && acCompanyFilter === "a6";
            const isUnkActive = filterStatus === "Active/Complete" && acCompanyFilter === "unknown";
            const anyAcSelected = filterStatus === "Active/Complete";
            const acCount = legendCounts["Active/Complete"] || 0;
            const { acGfOnly, acJoOnly, acBoth, acBest, acA6, acUnknown } = mapSubStats;
            const handleAcClick = () => {
              resetSubFilters();
              if (isAcActive) setFilterStatus("");
              else setFilterStatus("Active/Complete");
            };
            const handleSubClick = (type) => (e) => {
              e.stopPropagation();
              setPendingTeamFilter(null); setCancelReasonFilter(null); setReschedReasonFilter(null);
              setAcCompanyFilter(null);
              if (acSubFilter === type) { setFilterStatus(""); setAcSubFilter(null); }
              else { setFilterStatus("Active/Complete"); setAcSubFilter(type); }
            };
            const handleCoClick = (co) => (e) => {
              e.stopPropagation();
              setPendingTeamFilter(null); setCancelReasonFilter(null); setReschedReasonFilter(null);
              setAcSubFilter(null);
              if (acCompanyFilter === co) { setFilterStatus(""); setAcCompanyFilter(null); }
              else { setFilterStatus("Active/Complete"); setAcCompanyFilter(co); }
            };
            return (
              <div
                className={`rounded-lg px-3 py-2 text-center border bg-green-100 text-green-800 transition-all hover:shadow cursor-pointer ${
                  anyAcSelected ? 'ring-2 ring-blue-600 border-blue-600 shadow-md' : ''
                }`}
                style={{ opacity: acCount === 0 && !loading ? 0.5 : 1 }}
              >
                <div onClick={acCount > 0 ? handleAcClick : undefined} title="Show only Active/Complete job orders"
                  className={`cursor-pointer ${isAcActive ? 'font-black' : ''}`}>
                  <div className="flex items-baseline justify-center gap-1.5 leading-tight">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-700">Active/Complete</span>
                    <span className="text-lg font-bold text-gray-900 tabular-nums">{loading ? "..." : acCount}</span>
                  </div>
                </div>
                <div className="hidden sm:flex items-stretch gap-0 mt-0.5 border-t border-green-300 pt-1">
                  <div className="flex items-center justify-center gap-1 flex-1">
                    <button onClick={handleSubClick("gofiber_only")} disabled={acGfOnly === 0}
                      className={`text-[10px] leading-tight px-1 py-0.5 rounded transition-all ${
                        isGfActive ? 'bg-green-600 text-white font-bold shadow-sm' : 'hover:bg-green-200 text-green-900'
                      } ${acGfOnly === 0 ? 'opacity-40 cursor-default' : 'cursor-pointer'}`}
                      title="GoFiber Active but JO not Completed"
                    >
                      <span className="font-semibold">{acGfOnly}</span> GF
                    </button>
                    <span className="text-green-400 text-[10px]">|</span>
                    <button onClick={handleSubClick("jo_only")} disabled={acJoOnly === 0}
                      className={`text-[10px] leading-tight px-1 py-0.5 rounded transition-all ${
                        isJoActive ? 'bg-green-600 text-white font-bold shadow-sm' : 'hover:bg-green-200 text-green-900'
                      } ${acJoOnly === 0 ? 'opacity-40 cursor-default' : 'cursor-pointer'}`}
                      title="JO Completed but GoFiber not Active"
                    >
                      <span className="font-semibold">{acJoOnly}</span> JO
                    </button>
                    <span className="text-green-400 text-[10px]">|</span>
                    <button onClick={handleSubClick("both")} disabled={acBoth === 0}
                      className={`text-[10px] leading-tight px-1 py-0.5 rounded transition-all ${
                        isBothActive ? 'bg-green-600 text-white font-bold shadow-sm' : 'hover:bg-green-200 text-green-900'
                      } ${acBoth === 0 ? 'opacity-40 cursor-default' : 'cursor-pointer'}`}
                      title="Both GoFiber Active and JO Completed"
                    >
                      <span className="font-semibold">{acBoth}</span> Both
                    </button>
                  </div>
                  <div className="w-px bg-green-300 mx-1 self-stretch"></div>
                  <div className="flex items-center justify-center gap-1 flex-1">
                    <button onClick={handleCoClick("best")} disabled={acBest === 0}
                      className={`text-[10px] leading-tight px-1 py-0.5 rounded transition-all ${
                        isBestActive ? 'bg-green-600 text-white font-bold shadow-sm' : 'hover:bg-green-200 text-green-900'
                      } ${acBest === 0 ? 'opacity-40 cursor-default' : 'cursor-pointer'}`}
                      title="Completed by BEST teams"
                    >
                      <span className="font-semibold">{acBest}</span> BEST
                    </button>
                    <span className="text-green-400 text-[10px]">|</span>
                    <button onClick={handleCoClick("a6")} disabled={acA6 === 0}
                      className={`text-[10px] leading-tight px-1 py-0.5 rounded transition-all ${
                        isA6Active ? 'bg-green-600 text-white font-bold shadow-sm' : 'hover:bg-green-200 text-green-900'
                      } ${acA6 === 0 ? 'opacity-40 cursor-default' : 'cursor-pointer'}`}
                      title="Completed by A6 teams"
                    >
                      <span className="font-semibold">{acA6}</span> A6
                    </button>
                    <span className="text-green-400 text-[10px]">|</span>
                    <button onClick={handleCoClick("unknown")} disabled={acUnknown === 0}
                      className={`text-[10px] leading-tight px-1 py-0.5 rounded transition-all ${
                        isUnkActive ? 'bg-green-600 text-white font-bold shadow-sm' : 'hover:bg-green-200 text-green-900'
                      } ${acUnknown === 0 ? 'opacity-40 cursor-default' : 'cursor-pointer'}`}
                      title="Completed by unidentified teams or no team"
                    >
                      <span className="font-semibold">{acUnknown}</span> N/A
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}
          {/* Rescheduled card — only shown when selected date is today */}
          {isToday && (() => {
            const reschedCount = legendCounts.Rescheduled || 0;
            const reasonCounts = mapSubStats.reschedReasonCounts || {};
            const isMainActive = filterStatus === "Rescheduled" && !reschedReasonFilter;
            const anyActive = filterStatus === "Rescheduled";
            const handleMainClick = () => {
              resetSubFilters();
              if (isMainActive) setFilterStatus("");
              else setFilterStatus("Rescheduled");
            };
            const handleReasonClick = (reason) => (e) => {
              e.stopPropagation();
              setPendingTeamFilter(null); setAcSubFilter(null); setAcCompanyFilter(null); setCancelReasonFilter(null);
              if (reschedReasonFilter === reason) { setFilterStatus(""); setReschedReasonFilter(null); }
              else { setFilterStatus("Rescheduled"); setReschedReasonFilter(reason); }
            };
            const reasonEntries = Object.entries(reasonCounts).filter(([, c]) => c > 0);
            return (
              <div
                className={`rounded-lg px-3 py-2 text-center border bg-orange-100 text-orange-800 transition-all hover:shadow cursor-pointer ${
                  anyActive ? 'ring-2 ring-blue-600 border-blue-600 shadow-md' : ''
                }`}
                style={{ opacity: reschedCount === 0 && !loading ? 0.5 : 1 }}
              >
                <div onClick={reschedCount > 0 ? handleMainClick : undefined} title="Show only Rescheduled job orders"
                  className={`cursor-pointer ${isMainActive ? 'font-black' : ''}`}>
                  <div className="flex items-baseline justify-center gap-1.5 leading-tight">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-700">Rescheduled</span>
                    <span className="text-lg font-bold text-gray-900 tabular-nums">{loading ? "..." : reschedCount}</span>
                  </div>
                </div>
                {reasonEntries.length > 0 && (
                  <div className="hidden sm:flex flex-wrap items-center justify-center gap-1 mt-0.5 border-t border-orange-300 pt-1">
                    {reasonEntries.map(([reason, count], i) => (
                      <span key={reason} className="flex items-center gap-0">
                        {i > 0 && <span className="text-orange-400 text-[10px] mx-0.5">|</span>}
                        <button onClick={handleReasonClick(reason)}
                          className={`text-[10px] leading-tight px-1 py-0.5 rounded transition-all ${
                            reschedReasonFilter === reason ? 'bg-orange-600 text-white font-bold shadow-sm' : 'hover:bg-orange-200 text-orange-900'
                          } cursor-pointer`}
                          title={`Show rescheduled: ${reason}`}
                        >
                          <span className="font-semibold">{count}</span> {reason}
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
          {/* Cancelled card */}
          {(() => {
            const cancelCount = legendCounts.Cancelled || 0;
            const reasonCounts = mapSubStats.cancelReasonCounts || {};
            const isMainActive = filterStatus === "Cancelled" && !cancelReasonFilter;
            const anyActive = filterStatus === "Cancelled";
            const handleMainClick = () => {
              resetSubFilters();
              if (isMainActive) setFilterStatus("");
              else setFilterStatus("Cancelled");
            };
            const handleReasonClick = (reason) => (e) => {
              e.stopPropagation();
              setPendingTeamFilter(null); setAcSubFilter(null); setAcCompanyFilter(null); setReschedReasonFilter(null);
              if (cancelReasonFilter === reason) { setFilterStatus(""); setCancelReasonFilter(null); }
              else { setFilterStatus("Cancelled"); setCancelReasonFilter(reason); }
            };
            const reasonEntries = Object.entries(reasonCounts).filter(([, c]) => c > 0);
            return (
              <div
                className={`rounded-lg px-3 py-2 text-center border bg-red-100 text-red-800 transition-all hover:shadow cursor-pointer ${
                  anyActive ? 'ring-2 ring-blue-600 border-blue-600 shadow-md' : ''
                }`}
                style={{ opacity: cancelCount === 0 && !loading ? 0.5 : 1 }}
              >
                <div onClick={cancelCount > 0 ? handleMainClick : undefined} title="Show only Cancelled job orders"
                  className={`cursor-pointer ${isMainActive ? 'font-black' : ''}`}>
                  <div className="flex items-baseline justify-center gap-1.5 leading-tight">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-700">Cancelled</span>
                    <span className="text-lg font-bold text-gray-900 tabular-nums">{loading ? "..." : cancelCount}</span>
                  </div>
                </div>
                {reasonEntries.length > 0 && (
                  <div className="hidden sm:flex flex-wrap items-center justify-center gap-1 mt-0.5 border-t border-red-300 pt-1">
                    {reasonEntries.map(([reason, count], i) => (
                      <span key={reason} className="flex items-center gap-0">
                        {i > 0 && <span className="text-red-400 text-[10px] mx-0.5">|</span>}
                        <button onClick={handleReasonClick(reason)}
                          className={`text-[10px] leading-tight px-1 py-0.5 rounded transition-all ${
                            cancelReasonFilter === reason ? 'bg-red-600 text-white font-bold shadow-sm' : 'hover:bg-red-200 text-red-900'
                          } cursor-pointer`}
                          title={`Show cancelled: ${reason}`}
                        >
                          <span className="font-semibold">{count}</span> {reason}
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
          </div>
        </div>

        {/* ============================================================ */}
        {/* C. MAIN CONTENT: LEFT | CENTER MAP | RIGHT                   */}
        {/* ============================================================ */}
        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr_300px] gap-4 lg:flex-1 lg:min-h-0 lg:overflow-hidden">
          {/* ---------- LEFT PANEL: DEPLOYED TEAMS ---------- */}
          <div className="lg:overflow-y-auto lg:min-h-0">
            <DeployedTeamsPanel
              title="Deployed Teams"
              records={companyScopedPanelRecords}
              teamColorMap={teamColorMap}
              statusColors={STATUS_COLORS}
              isToday={isToday}
              historicalMode={!isToday}
              selectedRecordId={selectedRecord?.id ?? null}
              onJobOrderClick={handleJobOrderClick}
              onNameClick={handleSubscriberNameClick}
              showOtherCompany={showOtherCompanyTeams}
              onToggleOtherCompany={user?.company_id ? setShowOtherCompanyTeams : undefined}
            />
          </div>

          {/* ---------- CENTER: MAP ---------- */}
          <div className="bg-white rounded-lg shadow overflow-hidden relative isolate h-[500px] lg:h-full">
            {/* Compact status legend overlay (lower-left) */}
            {!loading && (
              <div className="absolute bottom-3 left-3 z-[1000] bg-white/95 backdrop-blur-sm rounded-md shadow-md border px-2 py-1.5 flex flex-col gap-0.5">
                {Object.entries(STATUS_COLORS)
                  .filter(([label]) => isToday || label !== "Rescheduled")
                  .map(([label, color]) => {
                    const isActive = filterStatus === label;
                    return (
                      <button
                        key={label}
                        onClick={() => { resetSubFilters(); setFilterStatus(isActive ? "" : label); }}
                        className={`flex items-center gap-1.5 text-[10.5px] rounded px-1 py-[2px] transition-colors ${
                          isActive ? "bg-gray-800 text-white font-semibold" : "hover:bg-gray-100 text-gray-700"
                        }`}
                        title={`Filter: ${label}`}
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0 ring-1 ring-white"
                          style={{ backgroundColor: color }}
                        />
                        <span>{label}</span>
                        <span className={`ml-auto tabular-nums text-[10px] ${isActive ? "text-gray-300" : "text-gray-400"}`}>
                          {legendCounts[label] || 0}
                        </span>
                      </button>
                    );
                  })}
              </div>
            )}
            {/* NAP View toggle + Measure tool (top-left) */}
            {!loading && (
              <div className="absolute top-3 left-12 z-[1000] flex items-center gap-1.5">
                {canViewNapOnNiMap && (niMapZoom >= 16 ? (
                  <button
                    onClick={() => setNapViewEnabled((v) => !v)}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md shadow-md border text-xs font-medium transition-colors ${
                      napViewEnabled
                        ? "bg-blue-600 text-white border-blue-600 hover:bg-blue-700"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                    }`}
                    title={napViewEnabled ? "Hide NAP points" : "Show NAP points on map"}
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    NAP {napViewEnabled ? "ON" : "OFF"}
                  </button>
                ) : (
                  <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md shadow-md border bg-gray-100 text-gray-400 border-gray-200 text-xs cursor-default">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    NAP (zoom in)
                  </div>
                ))}
                {/* Measure tool toggle */}
                <button
                  onClick={() => setMeasureEnabled((v) => !v)}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md shadow-md border text-xs font-medium transition-colors ${
                    measureEnabled
                      ? "bg-orange-500 text-white border-orange-500 hover:bg-orange-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                  title={measureEnabled ? "Turn off measure tool" : "Measure distance on map"}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21l6-6m0 0l4-4m-4 4l-4-4m4 4l4 4M21 3l-6 6m0 0l-4 4m4-4l4 4m-4-4l-4-4" />
                  </svg>
                  {measureEnabled ? `Measure: ${measureTotal < 1000 ? measureTotal.toFixed(1) + " m" : (measureTotal / 1000).toFixed(2) + " km"}` : "Measure"}
                </button>
                {measureEnabled && (
                  <button
                    onClick={() => { setMeasureEnabled(false); setMeasureTotal(0); }}
                    className="flex items-center px-1.5 py-1.5 rounded-md shadow-md border bg-white text-gray-500 border-gray-300 hover:bg-red-50 hover:text-red-500 text-xs"
                    title="Clear measurement & turn off"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            )}
            {loading ? (
              <div className="flex items-center justify-center h-full text-gray-400">
                Loading map...
              </div>
            ) : (
              <>
              {/* Subscriber location toast overlay */}
              {(subscriberLocating || subscriberLocateMsg) && (
                <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1100] max-w-sm">
                  {subscriberLocating && (
                    <div className="flex items-center gap-2 bg-white border border-blue-200 rounded-lg shadow-lg px-4 py-2.5 text-sm">
                      <svg className="w-4 h-4 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span className="text-blue-700 font-medium">Locating subscriber address…</span>
                    </div>
                  )}
                  {!subscriberLocating && subscriberLocateMsg && (
                    <div className={`flex items-center gap-2 rounded-lg shadow-lg px-4 py-2.5 text-sm border ${
                      subscriberLocateMsg.type === "success" ? "bg-green-50 border-green-200 text-green-700" :
                      subscriberLocateMsg.type === "warning" ? "bg-yellow-50 border-yellow-200 text-yellow-700" :
                      "bg-red-50 border-red-200 text-red-700"
                    }`}>
                      <span className="font-medium">{subscriberLocateMsg.text}</span>
                      <button
                        onClick={() => setSubscriberLocateMsg(null)}
                        className="ml-2 text-gray-400 hover:text-gray-600"
                      >✕</button>
                    </div>
                  )}
                </div>
              )}
              {/* Editing location toolbar */}
              {editingLocation && (
                <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1001] bg-blue-600 text-white px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium flex items-center gap-3">
                  {!editLocConfirm ? (
                    <>
                      📍 Click on the map to set new location
                      <button
                        onClick={() => setEditLocConfirm(true)}
                        disabled={!subscriberPin}
                        className="px-3 py-1 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed rounded text-xs font-semibold transition-colors"
                      >Save</button>
                      <button
                        onClick={() => { setEditingLocation(false); setEditLocConfirm(false); setSubscriberPin(null); setSubscriberLocateMsg(null); }}
                        className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded text-xs transition-colors"
                      >Cancel</button>
                    </>
                  ) : (
                    <>
                      <span className="text-xs">Save this as subscriber's location?</span>
                      <button
                        onClick={async () => {
                          const joId = subscriberPin?.id || selectedRecord?.id;
                          if (!joId || !subscriberPin) return;
                          setEditLocSaving(true);
                          try {
                            await api.put(`/ni/dashboard/${joId}/coordinates`, {
                              latitude: subscriberPin.lat,
                              longitude: subscriberPin.lng,
                            });
                            setSelectedRecord((prev) => prev ? { ...prev, latitude: subscriberPin.lat, longitude: subscriberPin.lng, location_saved: true } : prev);
                            setMapRecords((prev) =>
                              prev.map((rec) => rec.id === joId ? { ...rec, latitude: subscriberPin.lat, longitude: subscriberPin.lng, location_saved: true } : rec)
                            );
                            setSubscriberPin(null);
                            setSubscriberLocateMsg(null);
                            setEditingLocation(false);
                            setEditLocConfirm(false);
                          } catch {
                            alert("Failed to save coordinates.");
                          } finally {
                            setEditLocSaving(false);
                          }
                        }}
                        disabled={editLocSaving}
                        className="px-3 py-1 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 rounded text-xs font-semibold transition-colors"
                      >{editLocSaving ? "Saving…" : "Confirm"}</button>
                      <button
                        onClick={() => setEditLocConfirm(false)}
                        disabled={editLocSaving}
                        className="px-3 py-1 bg-white/20 hover:bg-white/30 disabled:opacity-50 rounded text-xs transition-colors"
                      >Back</button>
                    </>
                  )}
                </div>
              )}
              <MapContainer
                center={SERVICE_AREA_CENTER}
                zoom={SERVICE_AREA_ZOOM}
                maxZoom={21}
                style={{ height: "100%", minHeight: 500, background: "#ffffff", cursor: editingLocation ? "crosshair" : undefined }}
                scrollWheelZoom
              >
                {showBasemap && (
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    maxZoom={21}
                  />
                )}
                {showSatellite && (
                  <TileLayer
                    attribution="&copy; Google"
                    url="https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
                    maxZoom={21}
                  />
                )}
                {showStreetLabels && (
                  <TileLayer
                    key="road-labels"
                    url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png"
                    pane="overlayPane"
                    maxZoom={21}
                  />
                )}
                {showLandmarks && (
                  <TileLayer
                    key="landmarks"
                    attribution="&copy; Google"
                    url="https://mt1.google.com/vt/lyrs=h&x={x}&y={y}&z={z}"
                    pane="overlayPane"
                    maxZoom={21}
                  />
                )}

                {flyTarget && <FlyTo center={flyTarget} zoom={flyZoom} />}
                {panTarget && <PanTo target={panTarget} />}
                <NapPulseMarker position={focusNapPos} />
                <ZoomWatcher onZoomChange={(z) => { setShowLandmarks(z >= 17); setShowSatellite(z >= 20); }} />

                {/* City boundary layer */}
                {showCityBoundaries && cityGeoJSON && (
                  <GeoJSON
                    key="ni-city-boundaries"
                    data={cityGeoJSON}
                    interactive={false}
                    style={{
                      color: "#1e40af",
                      weight: 3,
                      fillColor: "#3b82f6",
                      fillOpacity: 0.04,
                      dashArray: "6 4",
                      interactive: false,
                    }}
                    onEachFeature={(feature, layer) => {
                      const name = feature.properties.city_name || "";
                      const shortName = name.replace(/^City of /i, "");
                      layer.bindTooltip(shortName, {
                        permanent: true,
                        direction: "center",
                        className: "ni-city-label-tooltip",
                      });
                    }}
                  />
                )}

                {/* Barangay boundary layer */}
                {showBrgyBoundaries && brgyGeoJSON && (
                  <GeoJSON
                    key="ni-brgy-boundaries"
                    data={brgyGeoJSON}
                    interactive={false}
                    style={{
                      color: "#6b7280",
                      weight: 1,
                      fillColor: "#d1d5db",
                      fillOpacity: 0.08,
                      interactive: false,
                    }}
                  />
                )}

                {/* Barangay name labels */}
                {showBrgyLabels && brgyGeoJSON && (
                  <BrgyLabels data={brgyGeoJSON} />
                )}

                {/* Head Office marker */}
                <Marker position={HEAD_OFFICE} icon={HEAD_OFFICE_ICON} pane="markerPane">
                  <Tooltip direction="top" offset={[0, -18]}>
                    <div className="text-xs font-bold">BEST Inc. Head Office</div>
                  </Tooltip>
                </Marker>

                {/* Dispatch team markers */}
                {filteredDispatchTeams.map((loc) => {
                  const coords = resolveCoordsGeo(loc.municipality, loc.barangay);
                  if (!coords) return null;
                  const key = `ni-${loc.municipality}-${loc.barangay}-teams`;
                  const teams = loc.teams;
                  const totalJobs = teams.reduce((s, t) => s + t.count, 0);
                  const labelHtml = teams.map((t) => {
                    const c = teamColorMap[t.team_number] || "#6b7280";
                    return `<span style="background:${c};color:#fff;padding:1px 5px;border-radius:3px;font-size:10px;font-weight:700;white-space:nowrap;box-shadow:0 1px 3px rgba(0,0,0,0.25);">${t.team_number}</span>`;
                  }).join(" ");

                  return (
                    <Marker
                      key={key}
                      position={coords}
                      pane="markerPane"
                      icon={L.divIcon({
                        className: "",
                        html: `<div style="display:flex;flex-direction:column;align-items:center;pointer-events:auto;">
                          ${showTeamLabels ? `<div style="display:flex;gap:3px;margin-bottom:2px;">${labelHtml}</div>` : ""}
                          <div style="
                            display:flex;align-items:center;justify-content:center;
                            width:26px;height:26px;border-radius:50%;
                            background:#1e293b;border:2px solid #fff;
                            box-shadow:0 2px 6px rgba(0,0,0,0.35);
                          "><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                            <circle cx="9" cy="7" r="4"/>
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                          </svg></div>
                        </div>`,
                        iconSize: [100, showTeamLabels ? 46 : 30],
                        iconAnchor: [50, showTeamLabels ? 46 : 30],
                      })}
                    >
                      <Tooltip direction="top" offset={[0, -48]}>
                        <div className="text-xs space-y-1">
                          <div className="font-bold">{loc.barangay} — {totalJobs} job(s)</div>
                          {teams.map((t) => (
                            <div key={t.team_number} className="flex items-center gap-1.5">
                              <span
                                className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0"
                                style={{ backgroundColor: teamColorMap[t.team_number] || "#6b7280" }}
                              />
                              <span className="font-medium">{t.team_number}</span>
                              <span className="text-gray-500">({t.count})</span>
                            </div>
                          ))}
                        </div>
                      </Tooltip>
                    </Marker>
                  );
                })}

                {/* Individual job order markers at exact lat/lng */}
                {filteredMapRecords.filter((r) => {
                  if (!r.latitude || !r.longitude) return false;
                  if (!showOtherCompanyTeams && user?.company_id && r.team_company_id && r.team_company_id !== user.company_id) return false;
                  return true;
                }).map((r) => {
                  const statusColor = STATUS_COLORS[r.status] || "#9ca3af";
                  const hasTeam = r.team_number && r.team_number.trim() && r.team_number.trim() !== "-";
                  const teamColor = hasTeam ? (teamColorMap[r.team_number] || "#6b7280") : null;
                  const teamLabel = hasTeam && showTeamLabels
                    ? `<div style="background:${teamColor};color:#fff;padding:0px 4px;border-radius:3px;font-size:8px;font-weight:700;white-space:nowrap;box-shadow:0 1px 2px rgba(0,0,0,0.25);line-height:14px;margin-bottom:1px;">${r.team_number}</div>`
                    : "";
                  return (
                    <Marker
                      key={`jo-${r.id}`}
                      position={[r.latitude, r.longitude]}
                      pane="markerPane"
                      icon={L.divIcon({
                        className: "",
                        html: `<div style="display:flex;flex-direction:column;align-items:center;pointer-events:auto;">
                          ${teamLabel}
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" style="filter:drop-shadow(0 1px 2px rgba(0,0,0,0.4));">
                            <path d="M12 3L2 12h3v8h6v-5h2v5h6v-8h3L12 3z" fill="${statusColor}" stroke="#fff" stroke-width="1.5"/>
                          </svg>
                        </div>`,
                        iconSize: [80, (hasTeam && showTeamLabels) ? 32 : 16],
                        iconAnchor: [40, (hasTeam && showTeamLabels) ? 32 : 16],
                      })}
                      eventHandlers={{
                        click: () => {
                          setSelectedRecord(r);
                          setSelectedNap(null);
                          setRightTab("subscriber");
                        },
                      }}
                    >
                      <Popup>
                        <div className="text-xs min-w-[160px]">
                          <div className="font-bold">{r.jo_number || r.account_number}</div>
                          <div
                            className="font-semibold text-blue-600 cursor-pointer hover:underline hover:text-blue-800"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.target.closest('.leaflet-popup')?.querySelector('.leaflet-popup-close-button')?.click();
                              handleLocateByAddress(r);
                            }}
                            title="Click to locate by address"
                          >
                            {r.name}
                          </div>
                          <div className="flex items-center gap-1 mt-0.5">
                            <span className="inline-block w-2 h-2 rounded-full" style={{ background: statusColor }} />
                            <span>{r.status}</span>
                          </div>
                          <div className="text-gray-600 mt-0.5">{r.complete_address || `${r.barangay}, ${r.municipality}`}</div>
                          <div className="text-gray-500">{r.barangay}, {r.municipality}</div>
                          {hasTeam
                            ? <div className="flex items-center gap-1 mt-0.5">
                                <span className="inline-block w-2 h-2 rounded-full" style={{ background: teamColor }} />
                                <span className="font-medium">{r.team_number}</span>
                              </div>
                            : <div className="text-gray-400 italic mt-0.5">No team assigned</div>
                          }
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}

                {/* Selected record marker */}
                {selectedRecord && (() => {
                  let coords = null;
                  if (selectedRecord.latitude && selectedRecord.longitude) {
                    coords = [selectedRecord.latitude, selectedRecord.longitude];
                  } else {
                    coords = resolveCoordsGeo(selectedRecord.municipality, selectedRecord.barangay);
                  }
                  if (!coords) return null;
                  return (
                    <CircleMarker
                      center={coords}
                      radius={8}
                      pathOptions={{
                        fillColor: "#3b82f6",
                        fillOpacity: 1,
                        color: "#1e40af",
                        weight: 3,
                      }}
                    >
                      <Popup>
                        <div className="text-xs min-w-[160px]">
                          <div className="font-bold">
                            {selectedRecord.jo_number || selectedRecord.account_number}
                          </div>
                          <div
                            className="font-semibold text-blue-600 cursor-pointer hover:underline hover:text-blue-800"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.target.closest('.leaflet-popup')?.querySelector('.leaflet-popup-close-button')?.click();
                              handleLocateByAddress(selectedRecord);
                            }}
                            title="Click to locate by address"
                          >
                            {selectedRecord.name}
                          </div>
                          <div className="flex items-center gap-1 mt-0.5">
                            <span className="inline-block w-2 h-2 rounded-full" style={{ background: STATUS_COLORS[selectedRecord.status] || "#9ca3af" }} />
                            <span>{selectedRecord.status}</span>
                          </div>
                          <div className="text-gray-600 mt-0.5">
                            {selectedRecord.barangay}, {selectedRecord.municipality}
                          </div>
                          <div className="text-gray-500 mt-0.5 italic">
                            {selectedRecord.team_number || "No team assigned"}
                          </div>
                        </div>
                      </Popup>
                    </CircleMarker>
                  );
                })()}

                {/* Subscriber search pin (from table view click) */}
                {subscriberPin && (
                  <Marker
                    position={[subscriberPin.lat, subscriberPin.lng]}
                    icon={SUBSCRIBER_SEARCH_ICON}
                    pane="markerPane"
                    zIndexOffset={2000}
                  >
                    <Popup>
                      <div className="text-xs space-y-0.5">
                        <div className="font-bold text-gray-800">{subscriberPin.name}</div>
                        <div className="text-gray-600">{subscriberPin.address || `${subscriberPin.barangay}, ${subscriberPin.municipality}`}</div>
                        {subscriberPin.joNumber && <div className="text-gray-500">JO: {subscriberPin.joNumber}</div>}
                      </div>
                    </Popup>
                  </Marker>
                )}

                {/* Click-to-repin handler */}
                <MapClickToPlace
                  active={editingLocation && !editLocConfirm}
                  onPlaced={(lat, lng) => {
                    setSubscriberPin((prev) => prev ? { ...prev, lat, lng } : null);
                  }}
                />

                {/* FST live location markers (desktop/admin only) */}
                {showFSTLocations && fstLocations.map((fst) => (
                  <Marker
                    key={`fst-loc-${fst.user_id}`}
                    position={[fst.latitude, fst.longitude]}
                    icon={buildFSTIcon(fst.is_active)}
                    pane="markerPane"
                  >
                    <Tooltip direction="top" offset={[0, -30]}>
                      <div className="text-xs space-y-0.5">
                        <div className="font-bold">{fst.display_name || fst.full_name || `User ${fst.user_id}`}</div>
                        <div className="flex items-center gap-1">
                          <span className={`inline-block w-2 h-2 rounded-full ${fst.is_active ? "bg-green-500" : "bg-gray-400"}`} />
                          <span>{fst.is_active ? "Active" : "Stale"}</span>
                        </div>
                        {fst.accuracy != null && <div className="text-gray-400">Accuracy: {Math.round(fst.accuracy)}m</div>}
                        {fst.updated_at && <div className="text-gray-400">Updated: {new Date(fst.updated_at).toLocaleTimeString()}</div>}
                      </div>
                    </Tooltip>
                  </Marker>
                ))}

                {/* NAP Overlay — desktop only, permission-gated */}
                {canViewNapOnNiMap && (
                  <>
                    <NiMapZoomTracker onZoomChange={setNiMapZoom} />
                    <NapOverlay
                      enabled={napViewEnabled && niMapZoom >= 16}
                      onPointClick={(pt) => {
                        setSelectedNap(pt);
                        setSelectedRecord(null);
                        setRightTab("napDetails");
                      }}
                      selectedNapId={selectedNap?.id || null}
                    />
                  </>
                )}

                {/* Measure Tool */}
                <MeasureTool enabled={measureEnabled} onTotalChange={setMeasureTotal} />
              </MapContainer>
              </>
            )}
          </div>

          {/* ---------- RIGHT PANEL ---------- */}
          <div className="flex flex-col min-h-0 lg:h-full">
            {/* Tab headers */}
            <MapPanelTabs
              accent="blue"
              active={rightTab}
              onChange={setRightTab}
              tabs={[
                { key: "layers", label: "Layers" },
                ...(canViewNapOnNiMap ? [{ key: "napDetails", label: "NAP Details" }] : []),
                { key: "subscriber", label: "Subscriber" },
                { key: "noTeams", label: "No Teams" },
              ]}
            />

            {/* Tab body */}
            <div className="bg-white rounded-b-lg shadow flex-1 lg:overflow-y-auto p-4">
              {/* Tab: Layers */}
              {rightTab === "layers" && (
                <div>
                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Map Layers</div>
                  <div className="space-y-0.5">
                    {[
                      ["Street Map", showBasemap, setShowBasemap],
                      ["Satellite", showSatellite, setShowSatellite],
                      ["Street / Road Names", showStreetLabels, setShowStreetLabels],
                      ["Landmarks", showLandmarks, setShowLandmarks],
                      ["City Boundaries", showCityBoundaries, setShowCityBoundaries],
                      ["Barangay Boundaries", showBrgyBoundaries, setShowBrgyBoundaries],
                      ["Barangay Names", showBrgyLabels, setShowBrgyLabels],
                      ["Team Labels", showTeamLabels, setShowTeamLabels],
                      ["Mobile Users", showFSTLocations, setShowFSTLocations],
                    ].map(([label, value, set]) => (
                      <label
                        key={label}
                        className="flex items-center gap-2.5 text-xs text-gray-700 cursor-pointer rounded-md px-2 py-1.5 hover:bg-blue-50/60 transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={value}
                          onChange={(e) => set(e.target.checked)}
                          className="w-4 h-4 rounded accent-blue-600"
                        />
                        <span className="font-medium">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Tab: NAP Details */}
              {rightTab === "napDetails" && canViewNapOnNiMap && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">
                    NAP Details
                  </h3>
                  <NapDetailsPanel
                    selectedPoint={selectedNap}
                    onClose={() => setSelectedNap(null)}
                    onRecordUpdated={() => {
                      // Force NapOverlay to refresh by toggling
                      setNapViewEnabled(false);
                      setTimeout(() => setNapViewEnabled(true), 50);
                    }}
                    onViewInTable={(napCode) => {
                      setNapTableModalSearch(napCode);
                    }}
                    compact
                  />
                </div>
              )}

              {/* Tab: Subscriber Detail */}
              {rightTab === "subscriber" && (
                <div>
                  {selectedRecord ? (
                    <RecordDetail
                      record={selectedRecord}
                      canEditTeam={canEditTeam}
                      canEditStatus={canEditStatus}
                      onTeamClick={handleTeamClick}
                      onStatusClick={handleStatusClick}
                      userCompanyId={user?.company_id}
                      onNapClick={async (napCode) => {
                        try {
                          const res = await api.get("/nap-locator", { params: { search: napCode, page: 1, page_size: 20 } });
                          const norm = (s) => s?.replace(/\s+/g, "").toUpperCase();
                          const match = res.data.find((n) => norm(n.nap_code) === norm(napCode));
                          if (!match) return;
                          const lat = parseFloat(match.latitude), lng = parseFloat(match.longitude);
                          if (Number.isNaN(lat) || Number.isNaN(lng)) return;
                          if (canViewNapOnNiMap) setNapViewEnabled(true);
                          setFocusNapPos([lat, lng]);
                          setFlyTarget([lat, lng]);
                          setFlyZoom(18);
                        } catch {
                          // silently ignore
                        }
                      }}
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center text-center py-12 px-4 text-gray-400">
                      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                        <svg className="w-6 h-6 text-gray-300" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                      </div>
                      <p className="text-xs leading-relaxed">
                        Click a job order or map marker<br />to view subscriber details.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Tab: Municipality Summary */}
              {rightTab === "noTeams" && (
                <NoTeamsPanel
                  records={noTeamRecords}
                  statusColors={STATUS_COLORS}
                  selectedRecordId={selectedRecord?.id ?? null}
                  onCardClick={handleNoTeamCardClick}
                  onNameClick={handleSubscriberNameClick}
                  onAssignTeam={handleTeamClick}
                  canAssignTeam={canEditTeam}
                />
              )}
            </div>
          </div>
        </div>
      </main>

      {/* === Inline editing modals (reuses same components as NI table) === */}

      {/* Status action menu */}
      {statusMenu && (
        <StatusActionMenu
          anchorRect={statusMenu.rect}
          onSelect={handleStatusAction}
          onClose={() => setStatusMenu(null)}
          isMobile={isMobile}
          visibleActions={canEditStatus}
        />
      )}

      {/* Complete dialog */}
      {directAction?.type === "complete" && (
        <QuickCompleteDialog
          key={directAction.jo.id}
          jo={directAction.jo}
          onDone={handleDirectActionDone}
          onCancel={() => setDirectAction(null)}
        />
      )}

      {/* Cancel dialog */}
      {directAction?.type === "cancel" && (
        <QuickCancelDialog
          key={directAction.jo.id}
          jo={directAction.jo}
          onDone={handleDirectActionDone}
          onCancel={() => setDirectAction(null)}
        />
      )}

      {/* Reschedule dialog */}
      {directAction?.type === "reschedule" && (
        <RescheduleModal
          key={directAction.jo.id}
          jobOrderId={directAction.jo.id}
          joNumber={directAction.jo.jo_number}
          currentJobDate={directAction.jo.job_date}
          ageingOriginDate={directAction.jo.ageing_origin_date}
          customerName={directAction.jo.customer_full_name || directAction.jo.name}
          customerAddress={directAction.jo.complete_address}
          initialRemark={directAction.jo.jo_status_remarks || ""}
          initialTeamNumber={directAction.jo.team_number || ""}
          initialPersonnelNames={directAction.jo.personnel_names || ""}
          initialPersonnel1Id={directAction.jo.personnel_1_id || null}
          initialPersonnel2Id={directAction.jo.personnel_2_id || null}
          teamAssignedByCompany={directAction.jo.team_assigned_by_company}
          onClose={() => setDirectAction(null)}
          onComplete={() => handleDirectActionDone()}
        />
      )}

      {/* Team assignment popover */}
      {teamMenu && (
        <TeamAssignmentPopover
          key={teamMenu.jo.id}
          anchorRect={teamMenu.rect}
          record={teamMenu.jo}
          assignmentDate={teamMenu.jo.job_date}
          apiPath="/job-orders"
          onSave={handleTeamSave}
          onClose={() => setTeamMenu(null)}
        />
      )}

      {/* NAP Table Modal (opened from "View on Table" in NAP details panel) */}
      {napTableModalSearch && (
        <NapTableModal
          key={napTableModalSearch}
          lcpCode={napTableModalSearch}
          onClose={() => setNapTableModalSearch(null)}
          onRecordUpdated={() => {
            setNapViewEnabled(false);
            setTimeout(() => setNapViewEnabled(true), 50);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function RecordDetail({ record, canEditTeam, canEditStatus, onTeamClick, onStatusClick, userCompanyId, onNapClick }) {
  const statusColor = {
    Pending: STATUS_COLORS.Pending,
    Completed: STATUS_COLORS["Active/Complete"],
    Cancelled: STATUS_COLORS.Cancelled,
    Rescheduled: STATUS_COLORS.Rescheduled,
  };
  const sColor = statusColor[record.jo_status] || "#9ca3af";
  const callColor = record.call_status ? getCallStatusColor(record.call_status) : null;
  const initials =
    (record.name || "?")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase() || "?";
  const isTeamLocked = !!(record.team_assigned_by_company && userCompanyId && record.team_assigned_by_company !== userCompanyId);
  const canClickStatus =
    canEditStatus && canEditStatus.length > 0 && record.jo_status !== "Completed" && record.jo_status !== "Cancelled";
  const pencil = (
    <svg className="w-3 h-3 text-gray-400 group-hover:text-blue-500 ml-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
    </svg>
  );

  return (
    <div className="space-y-3">
      {/* Hero */}
      <div className="rounded-xl bg-gradient-to-br from-blue-600 to-blue-500 p-3 text-white shadow-sm">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold flex-shrink-0">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold leading-tight truncate">{record.name || "—"}</div>
            <div className="text-[11px] text-blue-100 truncate">JO #{record.jo_number || "—"}</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 mt-2 text-[11px] text-blue-50">
          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z" />
            <circle cx="12" cy="11" r="3" />
          </svg>
          <span className="truncate">{[record.barangay, record.municipality].filter(Boolean).join(", ") || "—"}</span>
        </div>
      </div>

      {/* Editable status + team quick cards */}
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-lg border border-gray-100 bg-gray-50/60 p-2">
          <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-1">Status</div>
          {canClickStatus ? (
            <button onClick={(e) => onStatusClick(record, e)} className="group inline-flex items-center gap-1.5 rounded px-1 py-0.5 -ml-1 hover:bg-white transition-colors">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: sColor }} />
              <span className="text-xs font-semibold text-gray-800">{record.jo_status || "—"}</span>
              {pencil}
            </button>
          ) : (
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: sColor }} />
              <span className="text-xs font-semibold text-gray-800">{record.jo_status || "—"}</span>
            </span>
          )}
        </div>
        <div className="rounded-lg border border-gray-100 bg-gray-50/60 p-2">
          <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-1">Team</div>
          {canEditTeam && !isTeamLocked ? (
            <button onClick={(e) => onTeamClick(record, e)} className="group inline-flex items-center gap-1.5 rounded px-1 py-0.5 -ml-1 hover:bg-white transition-colors">
              <span className="text-xs font-semibold text-gray-800">{record.team_number || "—"}</span>
              {pencil}
            </button>
          ) : (
            <span className="text-xs font-semibold text-gray-800 inline-flex items-center gap-1">
              {record.team_number || "—"}
              {isTeamLocked && <span title="Locked — assigned by another company">🔒</span>}
            </span>
          )}
        </div>
      </div>

      <Section title="Account">
        <DetailRow label="Account No." value={record.account_number} mono />
        <DetailRow label="Reference ID" value={record.reference_id} mono />
        <DetailRow label="Plan" value={record.plan} />
      </Section>

      <Section title="Schedule & Crew">
        <DetailRow label="Job Date" value={record.job_date} />
        <DetailRow label="Personnel 1" value={record.personnel_1_name || "—"} />
        <DetailRow label="Personnel 2" value={record.personnel_2_name || "—"} />
        <div className="flex items-baseline justify-between gap-3 py-1.5">
          <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide flex-shrink-0">Call Status</span>
          <span className="text-[12.5px] font-medium text-gray-800 text-right inline-flex items-center gap-1.5">
            {record.call_status && callColor && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: callColor }} />}
            {record.call_status || "—"}
          </span>
        </div>
      </Section>

      <Section title="Location">
        <DetailRow label="Municipality" value={record.municipality} />
        <DetailRow label="Barangay" value={record.barangay} />
        <DetailRow label="Address" value={record.complete_address} />
      </Section>

      {(record.nap_code || record.nap_port) && (
        <Section title="NAP">
          {record.nap_code && (
            <div className="flex items-baseline justify-between gap-3 py-1.5">
              <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide flex-shrink-0">NAP Code</span>
              {onNapClick ? (
                <button
                  onClick={() => onNapClick(record.nap_code)}
                  className="text-[12.5px] font-medium text-blue-600 hover:text-blue-800 hover:underline text-right break-words min-w-0 flex items-center gap-1"
                >
                  <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z" />
                    <circle cx="12" cy="11" r="3" />
                  </svg>
                  {record.nap_code}
                </button>
              ) : (
                <span className="text-[12.5px] font-medium text-gray-800 text-right break-words min-w-0">{record.nap_code}</span>
              )}
            </div>
          )}
          <DetailRow label="NAP Port" value={record.nap_port} />
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-lg border border-gray-100 overflow-hidden">
      <div className="bg-gray-50 px-2.5 py-1.5 text-[9px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100">
        {title}
      </div>
      <div className="px-2.5 divide-y divide-gray-50">{children}</div>
    </div>
  );
}

function DetailRow({ label, value, bold, mono }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5">
      <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide flex-shrink-0">{label}</span>
      <span className={`text-gray-800 text-right break-words min-w-0 ${bold ? "font-bold" : "font-medium"} ${mono ? "font-mono text-[11.5px]" : "text-[12.5px]"}`}>
        {value}
      </span>
    </div>
  );
}

function CollapsiblePanel({ title, defaultOpen = false, badge, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between text-xs font-semibold text-gray-600 uppercase tracking-wide"
      >
        <span>{title}</span>
        <span className="flex items-center gap-1.5">
          {!open && badge != null && <span className="text-gray-400 font-normal normal-case">{badge}</span>}
          <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
        </span>
      </button>
      {open && <div className="mt-3">{children}</div>}
    </div>
  );
}

function HighBarangayPanel({ title, data, statusKey, resolveCoords, setFlyTarget, setFlyZoom, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const top = useMemo(() => {
    return [...data]
      .filter((b) => (b[statusKey] || 0) > 0)
      .sort((a, b) => (b[statusKey] || 0) - (a[statusKey] || 0))
      .slice(0, 5);
  }, [data, statusKey]);

  const total = useMemo(() => top.reduce((s, b) => s + (b[statusKey] || 0), 0), [top, statusKey]);

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between text-xs font-semibold text-gray-600 uppercase tracking-wide"
      >
        <span>{title}</span>
        <span className="flex items-center gap-1.5">
          {!open && <span className="text-gray-400 font-normal normal-case">{total}</span>}
          <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
        </span>
      </button>
      {open && (
        top.length === 0 ? (
          <p className="text-xs text-gray-400 mt-3">No data</p>
        ) : (
          <div className="space-y-1.5 mt-3">
            {top.map((b, i) => (
              <button
                key={`${b.municipality}-${b.barangay}`}
                onClick={() => {
                  const coords = resolveCoords(b.municipality, b.barangay, b.avg_lat, b.avg_lng);
                  if (coords) {
                    setFlyTarget([...coords]);
                    setFlyZoom(15);
                  }
                }}
                className="w-full flex items-center gap-2 text-xs hover:bg-gray-50 rounded px-1 py-1"
              >
                <span className="text-gray-400 w-4 text-right">{i + 1}.</span>
                <span className="text-gray-700 truncate flex-1 text-left">{b.barangay}</span>
                <span className="text-gray-400">{(b.municipality || "").replace(/^CITY OF\s+/i, "")}</span>
                <span className="font-medium text-gray-600 w-8 text-right">{b[statusKey]}</span>
              </button>
            ))}
          </div>
        )
      )}
    </div>
  );
}
